// SINGLE ENTRY AGGREGATED BUNDLE (IIFE OUTPUT)
// -----------------------------------------------------------------------------
// This file now imports its own SCSS and any auxiliary scripts (e.g. ripple) so the
// build can be a single classic <script> without type="module". Keep side-effect
// vendor imports first so gsap & plugins are defined before custom code runs.
// VENDOR FILES (converted from Gulp //=include to ES module imports for Vite)
// -----------------------------------------------------------------------------
// NOTE: These vendor scripts are local copies. Consider replacing with npm packages for easier updates.
import '../../source/vendor/bootstrap-5.3.8/dist/js/bootstrap.bundle.min.js';
// GSAP local UMD builds: import as side effects so Vite/Rollup doesn't expect ESM exports
import '../../source/vendor/gsap/minified/gsap.min.js'; // defines window.gsap
import '../../source/vendor/gsap/minified/ScrollToPlugin.min.js';
import '../../source/vendor/gsap/minified/ScrollSmoother.min.js';
import '../../source/vendor/gsap/minified/ScrollTrigger.min.js'; // defines window.ScrollTrigger
import '../../source/vendor/gsap/minified/DrawSVGPlugin.min.js';
import '../../source/vendor/gsap/minified/SplitText.min.js';
import '../../source/js/accessibility.js';

// CUSTOM CODE
// -----------------------------------------------------------------------------
// Register GSAP plugins (vendor UMDs populate globals)
if (typeof window.gsap !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger, ScrollSmoother, DrawSVGPlugin, ScrollToPlugin, SplitText);
}

class PostGrid {
    categoryFilter = null;

    constructor(postGridContainer) {
        // The Post Grid Container ID.
        this.postGrid = document.getElementById(postGridContainer);

        // The category filters at the top of the page, in the header.
        this.categoryFilters = document.querySelector('.js-filters');

        // Default REST API.
        this.restURL = '/wp-json/wp/v2/case-studies?&per_page=13';
    }

    // On page load, handle the initial call to the REST URL, create the cards, and populate the grid.
    // Add the listener for the category selection.
    async init() {
        // Check to see if there's a URL parameter to pre-sort the data.
        this.urlFilter = new URLSearchParams(document.location.search);
        if (this.urlFilter && this.urlFilter.get('type')) {
            this.categoryFilter = this.urlFilter.get('type');
            this.restURL = `/wp-json/wp/v2/case-studies?&service_type=${this.categoryFilter}&per_page=13`;

            // Add cs-filter class to the matching .js-filters li using the text of categoryFilter
            if (this.categoryFilters) {
                // Remove cs-filter from all first
                this.categoryFilters
                    .querySelectorAll('li.cs-filter')
                    .forEach((li) => li.classList.remove('cs-filter'));
                // Add cs-filter to the matching li
                const match = Array.from(this.categoryFilters.querySelectorAll('li')).find(
                    (li) =>
                        li.textContent.trim().toLowerCase() === this.categoryFilter.toLowerCase()
                );
                if (match) {
                    match.classList.add('cs-filter');
                }
            }
        }

        this.populateGrid();
        this.handleCategorySelection();
    }

    // Fetch posts from the REST API/
    async fetchPosts() {
        try {
            // Fetch the data fro the REST API.
            const response = await fetch(this.restURL);
            if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
            }
            const posts = await response.json();
            return posts;
        } catch (error) {
            console.error(error.message);
        }
    }

    // Helper function: format the post categories into li elements.
    formatPostCategories = (postCategories) => {
        let categoryListItems = [];

        postCategories.forEach((category) => {
            categoryListItems.push(
                `<li data-service="${category.slug}"><span class="menu-link-text">${category.name}</span></li>`
            );
        });

        return categoryListItems.join('');
    };

    // Create the HTML for the post card.
    createPostListings = (posts) => {
        let renderedPosts = [];
        posts.forEach((post) => {
            // Get the category list and format the data using a helper function.
            this.formatPostCategories(post.category);
            let renderedPost = `<div id="${post.id}" class="work-single">
                <div class="work-single-inner" data-service>
                    <a href="${post.link}" class="work-single-header">
                        <img src="${post.featured_image_url}" alt="${post.featured_image_alt}" />
                        <div class="work-single-header-inner">
                            <h2 class="work-single-title">${post.title.rendered}</h2>
                        </div>
                    </a>
                    <div class="work-single-content">
                        ${post.excerpt.rendered}
                        <div class="wp-block-button is-style-arrow-cta-dark"><a class="wp-block-button__link wp-element-button" href="${post.link}"><span class="arrow-cta-regular">See the work</span><span class="arrow-cta-bold">See the work</span><span class="arrow-cta-svg"><svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="-2 -2 14.52 18.09"><defs><style>.arrow-cta-dark-1{ fill: none; stroke: #000; stroke-width: 1.5px} </style></defs><path class="arrow-cta-dark-1" d="M9.68.31l4.01,8.73-4.01,8.73"></path><path class="arrow-cta-dark-1" d="M.68.31l4.01,8.73L.68,17.78"></path></svg></span></a></div>
                    </div>
                </div>
            </div>`;
            renderedPosts.push(renderedPost);
        });

        return renderedPosts;
    };

    // Add the posts to the post grid.
    async populateGrid() {
        // Start by getting the posts from the REST API,
        // and then process the data into a card for the post grid.
        const posts = await this.fetchPosts();
        const postListings = this.createPostListings(posts);

        // Empty the post grid container to prevent duplicate cards,
        // and then populate the grid with the new cards.
        this.postGrid.innerHTML = '';

        postListings.forEach((card) => {
            this.postGrid.insertAdjacentHTML('beforeend', card);
        });

        // Only add .work-singles-filtered if the URL contains a filter string
        const parent = this.postGrid.closest('.work-content');
        const urlParams = new URLSearchParams(window.location.search);
        if (parent) {
            if (urlParams.has('type') && urlParams.get('type')) {
                parent.classList.add('work-singles-filtered');
            } else {
                parent.classList.remove('work-singles-filtered');
            }
        }

        // Re-rendering the grid changes the page height. GSAP ScrollTriggers
        // (including the footer "fade in up" reveal) cache their start/end
        // positions, so without a refresh the footer legal links can stay stuck
        // at opacity:0 and never appear when the page becomes shorter. Recalculate
        // trigger positions for the new layout, once now and again after the card
        // images load (which is what finalizes the grid height).
        this.refreshScrollTriggers();
    }

    // Recalculate GSAP ScrollTrigger positions after the grid re-renders so that
    // scroll-reveal animations lower on the page (notably the footer legal links)
    // fire correctly for the new, often shorter, layout height.
    refreshScrollTriggers = () => {
        if (typeof ScrollTrigger === 'undefined') return;

        // Refresh immediately for the new (pre-image) layout.
        requestAnimationFrame(() => ScrollTrigger.refresh());

        // Card images finalize the grid height as they load; refresh again once
        // the last outstanding image resolves so trigger positions match the
        // final layout.
        const images = Array.from(this.postGrid.querySelectorAll('img'));
        let pending = images.filter((img) => !img.complete).length;
        if (pending === 0) return;

        images.forEach((img) => {
            if (img.complete) return;
            const onSettled = () => {
                pending -= 1;
                if (pending === 0) ScrollTrigger.refresh();
            };
            img.addEventListener('load', onSettled, { once: true });
            img.addEventListener('error', onSettled, { once: true });
        });
    };

    // Filters the posts by category on click.
    handleCategorySelection = () => {
        document.addEventListener('click', (e) => {
            // Find the closest li with data-service, in case a child span was clicked
            const li = e.target.closest('li[data-service]');
            if (!li) {
                // Prevent the code from firing if no service is clicked on.
                return;
            }
            const selected = li.dataset.service;
            const filter = selected.toLowerCase();

            // Toggle the filter with the active class
            const currentActive = this.categoryFilters.querySelector('li.cs-filter');
            if (currentActive) currentActive.classList.toggle('cs-filter');
            this.categoryFilters
                .querySelector(`li[data-service="${selected}"]`)
                .classList.toggle('cs-filter');

            // Update the URL with the filter
            const url = new URL(window.location);
            if (filter === 'all') {
                // If "all" is selected, reset the filter to show all posts.
                this.categoryFilter = null;
                this.restURL = '/wp-json/wp/v2/case-studies?&per_page=13';
                url.searchParams.delete('type');
                window.history.replaceState({}, '', url);
                this.populateGrid();
                gsap.to(window, { scrollTo: { y: 0 }, duration: 0.7, ease: 'power2.out' });
            } else {
                // Set the category filter, and update the REST URL to include it for filtering..
                this.categoryFilter = filter;
                this.restURL = `/wp-json/wp/v2/case-studies?&service_type=${this.categoryFilter}&per_page=13`;
                url.searchParams.set('type', filter);
                window.history.replaceState({}, '', url);
                this.populateGrid();
                gsap.to(window, { scrollTo: { y: 0 }, duration: 0.7, ease: 'power2.out' });
            }
        });
    };
}

// Helper functions for media queries (moved outside DOMContentLoaded to be accessible before fonts load)
function isDesktop() {
    return window.matchMedia('(min-width: 1023px)').matches;
}

function isMobile() {
    return window.matchMedia('(max-width: 1022px)').matches;
}

(function () {
    function onReady(callback) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', callback, { once: true });
            return;
        }

        callback();
    }

    function debounce(fn, wait) {
        var timeoutId;

        return function () {
            var args = arguments;
            clearTimeout(timeoutId);
            timeoutId = setTimeout(function () {
                fn.apply(null, args);
            }, wait);
        };
    }

    function getMasonryColumnCount(container) {
        var styles = window.getComputedStyle(container);
        var columnCount = styles ? styles.getPropertyValue('column-count') : null;
        var parsed = columnCount && columnCount !== 'auto' ? parseInt(columnCount, 10) : 1;

        return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    function getMasonryCardSelector(container) {
        return container.getAttribute('data-pi-masonry-card-selector') || '.masonry-card';
    }

    function elementMatchesSelector(element, selector) {
        if (!element || typeof element.matches !== 'function') {
            return false;
        }

        try {
            return element.matches(selector);
        } catch {
            return false;
        }
    }

    function ensureMasonryHelper() {
        if (typeof window.piReorderMasonry !== 'function') {
            window.piReorderMasonry = function (containerOrSelector, columns) {
                var container =
                    typeof containerOrSelector === 'string'
                        ? document.querySelector(containerOrSelector)
                        : containerOrSelector;

                if (!container) {
                    return;
                }

                var parsed = columns && columns > 0 ? columns : getMasonryColumnCount(container);
                if (!parsed || parsed < 2) {
                    return;
                }

                if (container.getAttribute('data-pi-masonry-columns') === String(parsed)) {
                    return;
                }

                var cardSelector = getMasonryCardSelector(container);
                var allChildren = Array.prototype.slice.call(container.children || []);
                var cardChildren = allChildren.filter(function (child) {
                    return elementMatchesSelector(child, cardSelector);
                });
                if (!cardChildren.length) {
                    return;
                }

                var otherChildren = allChildren.filter(function (child) {
                    return !elementMatchesSelector(child, cardSelector);
                });

                container.__piReordering = true;

                var reorderedCards = [];
                for (var columnIndex = 0; columnIndex < parsed; columnIndex++) {
                    for (
                        var childIndex = columnIndex;
                        childIndex < cardChildren.length;
                        childIndex += parsed
                    ) {
                        reorderedCards.push(cardChildren[childIndex]);
                    }
                }

                reorderedCards.forEach(function (child) {
                    container.appendChild(child);
                });

                otherChildren.forEach(function (child) {
                    container.appendChild(child);
                });

                container.setAttribute('data-pi-masonry-columns', String(parsed));

                setTimeout(function () {
                    container.__piReordering = false;
                }, 50);

                if (!container.__piObserver) {
                    var observer = new MutationObserver(function (mutations) {
                        if (container.__piReordering) {
                            return;
                        }

                        var shouldReorder = mutations.some(function (mutation) {
                            return (
                                (mutation.type === 'attributes' &&
                                    mutation.attributeName === 'class') ||
                                mutation.type === 'childList'
                            );
                        });

                        if (!shouldReorder) {
                            return;
                        }

                        try {
                            var currentColumns = getMasonryColumnCount(container);
                            if (currentColumns > 1) {
                                window.piReorderMasonry(container, currentColumns);
                            }
                        } catch {
                            // Ignore transient DOM read errors.
                        }
                    });

                    observer.observe(container, {
                        attributes: true,
                        attributeFilter: ['class'],
                        childList: true,
                    });
                    container.__piObserver = observer;
                }
            };
        }

        if (window.__piMasonryResizeHandlerBound) {
            return;
        }

        window.__piMasonryResizeHandlerBound = true;
        window.addEventListener(
            'resize',
            debounce(function () {
                var isDesktop = window.matchMedia('(min-width: 993px)').matches;
                if (!isDesktop) {
                    return;
                }

                document
                    .querySelectorAll('[data-pi-masonry-reorder="1"]')
                    .forEach(function (container) {
                        var columnCount = getMasonryColumnCount(container);
                        if (columnCount > 1) {
                            window.piReorderMasonry(container, columnCount);
                        }
                    });
            }, 200)
        );
    }

    function initMasonryContainers() {
        ensureMasonryHelper();

        document.querySelectorAll('[data-pi-masonry-reorder="1"]').forEach(function (container) {
            if (container.dataset.piMasonryReady === 'true') {
                return;
            }

            container.dataset.piMasonryReady = 'true';

            try {
                var columnCount = getMasonryColumnCount(container);
                if (window.matchMedia('(min-width: 993px)').matches && columnCount > 1) {
                    window.piReorderMasonry(container, columnCount);
                }
            } catch {
                // Ignore transient DOM read errors.
            }
        });
    }

    function initArchiveHeaderFilter() {
        var categoryList = document.getElementById('archive-header-filters');
        if (!categoryList || categoryList.dataset.piArchiveFilterReady === 'true') {
            return;
        }

        categoryList.dataset.piArchiveFilterReady = 'true';

        var maxVisible = parseInt(categoryList.getAttribute('data-max-visible'), 10);
        if (!Number.isFinite(maxVisible) || maxVisible < 1) {
            maxVisible = 3;
        }

        (function markCurrentFilter() {
            var currentUrl = window.location.pathname;
            var urlSegments = currentUrl.split('/').filter(Boolean);
            var lastSegment = '';

            for (var segmentIndex = urlSegments.length - 1; segmentIndex >= 0; segmentIndex--) {
                var segment = urlSegments[segmentIndex];
                if (segment === 'page' || /^\d+$/.test(segment)) {
                    continue;
                }

                lastSegment = segment;
                break;
            }

            var isAll =
                currentUrl.endsWith('/our-insights') || currentUrl.endsWith('/our-insights/');
            var filterLinks = categoryList.querySelectorAll('li.cat-item-original a');
            var currentItem = null;

            filterLinks.forEach(function (link) {
                var href = link.getAttribute('href') || '';
                var hrefSegments = href.split('/').filter(Boolean);
                var hrefLast = hrefSegments[hrefSegments.length - 1] || '';
                var parentItem = link.closest('li');

                if (!parentItem) {
                    return;
                }

                parentItem.classList.remove('current');

                if (isAll && href.endsWith('/our-insights')) {
                    parentItem.classList.add('current');
                    currentItem = parentItem;
                } else if (lastSegment && lastSegment === hrefLast) {
                    parentItem.classList.add('current');
                    currentItem = parentItem;
                }
            });

            if (currentItem) {
                var allItem = categoryList.querySelector('li[data-service="all"]');
                if (allItem && allItem.nextSibling !== currentItem) {
                    categoryList.insertBefore(currentItem, allItem.nextSibling);
                }
            }
        })();

        function resizeCategoryMenu() {
            var originalItems = Array.from(categoryList.querySelectorAll('li.cat-item-original'));
            var moreTopicsItem = categoryList.querySelector('li.more-topics');
            if (moreTopicsItem) {
                moreTopicsItem.remove();
            }

            categoryList.querySelectorAll('li.cat-item-original').forEach(function (item) {
                item.remove();
            });

            originalItems.forEach(function (item) {
                categoryList.appendChild(item);
            });

            if (window.innerWidth <= 768 || originalItems.length <= maxVisible) {
                return;
            }

            var triggerId = 'more-topics-trigger';
            var subMenuId = 'more-topics-submenu';
            var moreItem = document.createElement('li');
            moreItem.className = 'has-children more-topics';
            moreItem.innerHTML =
                '<a href="#" tabindex="0" class="has-children-trigger" role="button" aria-haspopup="true" aria-expanded="false" aria-controls="' +
                subMenuId +
                '" id="' +
                triggerId +
                '"><span class="menu-link-text">More topics</span></a>';

            var subMenu = document.createElement('ul');
            subMenu.className = 'sub-menu';
            subMenu.id = subMenuId;

            originalItems.slice(maxVisible).forEach(function (item) {
                subMenu.appendChild(item);
            });

            moreItem.appendChild(subMenu);
            categoryList.appendChild(moreItem);

            var trigger = moreItem.querySelector('a');
            if (!trigger) {
                return;
            }

            trigger.addEventListener('click', function (event) {
                event.preventDefault();
                var isOpen = moreItem.classList.toggle('is-open');
                trigger.classList.toggle('active', isOpen);
                trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

                if (isOpen) {
                    var firstSubItem = subMenu.querySelector('a');
                    if (firstSubItem) {
                        firstSubItem.focus();
                    }
                }
            });

            trigger.addEventListener('keydown', function (event) {
                if (event.key === ' ' || event.key === 'Enter') {
                    event.preventDefault();
                    trigger.click();
                } else if (event.key === 'Escape') {
                    moreItem.classList.remove('is-open');
                    trigger.classList.remove('active');
                    trigger.setAttribute('aria-expanded', 'false');
                    trigger.focus();
                }
            });

            if (!categoryList.__piMoreTopicsListenerAdded) {
                document.addEventListener('click', function (event) {
                    var openItem = categoryList.querySelector('li.more-topics.is-open');
                    if (openItem && !openItem.contains(event.target)) {
                        var openTrigger = openItem.querySelector('a.has-children-trigger');
                        openItem.classList.remove('is-open');
                        if (openTrigger) {
                            openTrigger.classList.remove('active');
                            openTrigger.setAttribute('aria-expanded', 'false');
                        }
                    }
                });

                categoryList.__piMoreTopicsListenerAdded = true;
            }

            categoryList.classList.add('is-ready');
        }

        window.addEventListener('resize', resizeCategoryMenu);
        resizeCategoryMenu();
    }

    function initInsightsGrid() {
        var root = document.querySelector('[data-pi-insights-grid="1"]');
        if (!root || root.dataset.piInsightsGridReady === 'true') {
            return;
        }

        root.dataset.piInsightsGridReady = 'true';
        ensureMasonryHelper();

        var gridContainer = root.querySelector('.masonry-columns');
        var filterButtons = root.querySelectorAll('.archive-grid-filter-button');
        var viewAllContainer = root.querySelector('.archive-grid-footer');
        var viewAllLink = viewAllContainer
            ? viewAllContainer.querySelector('.archive-grid-view-all')
            : null;
        var restRoot = root.getAttribute('data-rest-root') || '';
        var pageSize = parseInt(root.getAttribute('data-page-size'), 10);
        var insightsChildIds = [];
        var insightsChildSlugs = [];

        if (!gridContainer || !filterButtons.length || !restRoot) {
            return;
        }

        if (!Number.isFinite(pageSize) || pageSize < 1) {
            pageSize = 6;
        }

        try {
            insightsChildIds = JSON.parse(root.getAttribute('data-insights-child-ids') || '[]');
        } catch {
            insightsChildIds = [];
        }

        try {
            insightsChildSlugs = JSON.parse(root.getAttribute('data-insights-child-slugs') || '[]');
        } catch {
            insightsChildSlugs = [];
        }

        var loadingMarkup =
            '<div class="archive-grid-loading"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></div>';
        var baseViewAllLabel = viewAllLink
            ? viewAllLink.getAttribute('data-default-label') || 'View all'
            : 'View all';
        var viewAllLabelTargets = viewAllLink
            ? Array.prototype.slice.call(
                  viewAllLink.querySelectorAll('.arrow-cta-regular, .arrow-cta-bold')
              )
            : [];

        function setViewAllVisibility(totalItems) {
            if (!viewAllContainer) {
                return;
            }

            var total = parseInt(totalItems, 10);
            if (!Number.isFinite(total)) {
                total = 0;
            }

            if (total > pageSize) {
                viewAllContainer.removeAttribute('hidden');
            } else {
                viewAllContainer.setAttribute('hidden', 'hidden');
            }
        }

        function updateViewAllLabel(targetButton) {
            if (!viewAllLink || !targetButton) {
                return;
            }

            var slug = targetButton.getAttribute('data-cat-slug') || '';
            var postType = targetButton.getAttribute('data-post-type') || 'post';
            var menuTextElement = targetButton.querySelector('.menu-link-text');
            var menuText = menuTextElement ? menuTextElement.textContent.trim() : '';
            var label = baseViewAllLabel;
            var shouldAppend = menuText && slug && slug !== 'all';

            if (!shouldAppend && menuText && postType !== 'post' && postType !== 'both') {
                shouldAppend = true;
            }

            if (shouldAppend) {
                label = baseViewAllLabel + ' ' + menuText;
            }

            viewAllLabelTargets.forEach(function (element) {
                element.textContent = label;
            });
            viewAllLink.setAttribute('aria-label', label);
        }

        function setActiveFilter(targetButton) {
            filterButtons.forEach(function (button) {
                button.setAttribute('aria-pressed', 'false');
                if (button.parentElement) {
                    button.parentElement.classList.remove('is-active');
                }
            });

            targetButton.setAttribute('aria-pressed', 'true');
            if (targetButton.parentElement) {
                targetButton.parentElement.classList.add('is-active');
            }
        }

        function buildCategoriesMarkup(terms) {
            if (!terms || !terms.length) {
                return '';
            }

            var filtered = terms.filter(function (term) {
                if (!term) {
                    return false;
                }

                var id = term.id || term.term_id || 0;
                var slug = term.slug || '';

                if (Number.isFinite(Number(id)) && insightsChildIds.indexOf(Number(id)) !== -1) {
                    return true;
                }

                return !!slug && insightsChildSlugs.indexOf(slug) !== -1;
            });

            if (!filtered.length) {
                return '';
            }

            var listItems = filtered
                .map(function (term) {
                    return '<li><a href="' + term.link + '">' + term.name + '</a></li>';
                })
                .join('');

            return '<ul class="masonry-categories">' + listItems + '</ul>';
        }

        function buildCard(post) {
            var formatter = new Intl.DateTimeFormat('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            });
            var postDate = formatter.format(new Date(post.date));
            var title = post.title && post.title.rendered ? post.title.rendered : 'Untitled';
            var permalink = post.link;
            var categories = [];

            if (post._embedded && post._embedded['wp:term'] && post._embedded['wp:term'][0]) {
                categories = post._embedded['wp:term'][0]
                    .filter(function (term) {
                        if (!term) {
                            return false;
                        }

                        var id = term.id || term.term_id || 0;
                        var slug = term.slug || '';

                        if (
                            Number.isFinite(Number(id)) &&
                            insightsChildIds.indexOf(Number(id)) !== -1
                        ) {
                            return true;
                        }

                        return !!slug && insightsChildSlugs.indexOf(slug) !== -1;
                    })
                    .map(function (term) {
                        return {
                            id: term.id || term.term_id || 0,
                            slug: term.slug || '',
                            name: term.name,
                            link: term.link || '',
                        };
                    });
            }

            var categoriesMarkup = buildCategoriesMarkup(categories);
            var imageMarkup = '';

            if (
                post._embedded &&
                post._embedded['wp:featuredmedia'] &&
                post._embedded['wp:featuredmedia'][0] &&
                post._embedded['wp:featuredmedia'][0].source_url
            ) {
                var media = post._embedded['wp:featuredmedia'][0];
                var altText = media.alt_text
                    ? media.alt_text
                    : media.title && media.title.rendered
                      ? media.title.rendered
                      : '';
                imageMarkup =
                    '<a class="masonry-featured-image" href="' +
                    permalink +
                    '" aria-hidden="false">' +
                    '<img src="' +
                    media.source_url +
                    '" alt="' +
                    altText +
                    '">' +
                    '</a>';
            }

            return (
                '<article class="post-' +
                post.id +
                ' masonry-card"><div class="masonry-card-inner">' +
                imageMarkup +
                '<div class="masonry-card-body">' +
                '<p class="masonry-date">' +
                postDate +
                '</p>' +
                '<h3 class="masonry-heading"><a href="' +
                permalink +
                '">' +
                title +
                '</a></h3>' +
                categoriesMarkup +
                '</div></div></article>'
            );
        }

        function renderPosts(posts) {
            if (!posts.length) {
                gridContainer.innerHTML = '<p class="no-results">No insights found.</p>';
                return;
            }

            if (Array.isArray(posts) && posts.length <= 4) {
                gridContainer.innerHTML = posts.map(buildCard).join('');
                if (gridContainer.__piObserver) {
                    gridContainer.__piObserver.disconnect();
                    gridContainer.__piObserver = null;
                }
                gridContainer.removeAttribute('data-pi-masonry-columns');
                gridContainer.removeAttribute('data-pi-masonry-reorder');
                return;
            }

            var columns = getMasonryColumnCount(gridContainer) || 3;

            function reorderColumnMajor(items, cols) {
                if (!Array.isArray(items) || cols < 1) {
                    return items;
                }

                var reordered = [];
                for (var columnIndex = 0; columnIndex < cols; columnIndex++) {
                    for (var itemIndex = columnIndex; itemIndex < items.length; itemIndex += cols) {
                        reordered.push(items[itemIndex]);
                    }
                }

                return reordered;
            }

            var orderedPosts = reorderColumnMajor(posts, columns);
            gridContainer.innerHTML = orderedPosts.map(buildCard).join('');
            gridContainer.setAttribute('data-pi-masonry-reorder', '1');
            gridContainer.setAttribute('data-pi-masonry-columns', String(columns));

            try {
                if (window.matchMedia('(min-width: 993px)').matches && columns > 1) {
                    window.piReorderMasonry(gridContainer, columns);
                }
            } catch {
                // Ignore transient DOM read errors.
            }
        }

        function fetchEndpoint(endpoint, categoryId) {
            var url = endpoint + '?per_page=' + pageSize + '&_embed=1&status=publish';

            if (endpoint.indexOf('/posts') !== -1) {
                url += '&ignore_sticky_posts=1';
            }

            url += '&orderby=date&order=desc';

            if (categoryId) {
                url += '&categories=' + encodeURIComponent(categoryId);
            }

            return fetch(url, { credentials: 'same-origin' }).then(function (response) {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                var total = parseInt(response.headers.get('X-WP-Total'), 10);
                return response.json().then(function (data) {
                    return { data: data, total: total };
                });
            });
        }

        function fetchPostsForCategory(button) {
            setActiveFilter(button);

            var categoryId = button.getAttribute('data-cat-id');
            var postType = button.getAttribute('data-post-type') || 'post';
            var link = button.getAttribute('data-cat-link');

            if (viewAllLink) {
                var fallback = viewAllLink.getAttribute('data-default-link');
                viewAllLink.setAttribute('href', link ? link : fallback);
                updateViewAllLabel(button);
            }

            gridContainer.innerHTML = loadingMarkup;

            if (postType === 'both') {
                Promise.all([
                    fetchEndpoint(restRoot + 'posts', categoryId),
                    fetchEndpoint(restRoot + 'whitepaper', categoryId),
                ])
                    .then(function (results) {
                        var combined = [];
                        var totalItems = 0;

                        results.forEach(function (result) {
                            if (result && result.data) {
                                combined = combined.concat(result.data);
                                totalItems += Number.isFinite(result.total)
                                    ? result.total
                                    : result.data.length;
                            }
                        });

                        combined.sort(function (a, b) {
                            var aDate = a.date_gmt || a.date || '';
                            var bDate = b.date_gmt || b.date || '';
                            return Date.parse(bDate) - Date.parse(aDate);
                        });

                        renderPosts(combined.slice(0, pageSize));
                        setViewAllVisibility(totalItems);
                    })
                    .catch(function () {
                        gridContainer.innerHTML =
                            '<p class="no-results">Unable to load insights right now.</p>';
                        setViewAllVisibility(0);
                    });

                return;
            }

            var endpoint = restRoot + (postType === 'post' ? 'posts' : postType);
            fetchEndpoint(endpoint, categoryId)
                .then(function (payload) {
                    renderPosts(payload.data);
                    setViewAllVisibility(
                        Number.isFinite(payload.total) ? payload.total : payload.data.length
                    );
                })
                .catch(function () {
                    gridContainer.innerHTML =
                        '<p class="no-results">Unable to load insights right now.</p>';
                    setViewAllVisibility(0);
                });
        }

        filterButtons.forEach(function (button) {
            button.addEventListener('click', function (event) {
                event.preventDefault();
                if (button.getAttribute('aria-pressed') === 'true') {
                    return;
                }

                fetchPostsForCategory(button);
            });
        });

        var activeButton = root.querySelector('.archive-grid-filter-button[aria-pressed="true"]');
        if (activeButton) {
            updateViewAllLabel(activeButton);
        }
    }

    onReady(function () {
        initArchiveHeaderFilter();
        initMasonryContainers();
        initInsightsGrid();
    });
})();

document.addEventListener('DOMContentLoaded', () => {
    if (typeof window.gsap === 'undefined') return; // abort if gsap missing

    // Wait for fonts to load before using SplitText to avoid "SplitText called before fonts loaded" error
    document.fonts.ready.then(() => {
        initializeAnimations();
    });

    function initializeAnimations() {
        if (document.body.classList.contains('home')) {
            ScrollTrigger.config({ ignoreMobileResize: true });
            ScrollTrigger.normalizeScroll(true);
        }

        // Define excluded classes globally so both functions can use it.
        // `.footer *` is excluded because page-height changes (e.g. the Work grid
        // re-rendering) can leave the footer's fade-in reveal stuck at opacity:0.
        const excludedClasses = [
            '.home-intro-rotators p, .hero-home .wp-block-video video, .sidebar-sticky .wp-block-button__link, .sidebar-sticky p, .is-style-quote-animated svg, .footer-brand svg, .footer *, .menu-primary-list .menu-item, .menu-primary-list a, .hero-cover .wp-block-post-terms a, .hero-cover .wp-block-cover__image-background, .work-single-categories li,  .query-loop .wp-block-post-template .wp-block-post, .is-style-arrow-cta-dark .wp-block-button__link .arrow-cta-svg svg, .hs-form-booleancheckbox, .hbspt-form p, .hbspt-form li, .work-single:first-child p, .work-single:first-child .wp-block-button__link, .work-single img, .hero-element-pause, .hero-element-trigger, div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner p, div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner a, .logo-row figure img, .text-image-split .wp-block-image',
        ];

        // Animate all headings and paragraphs as you scroll to them
        const scrollFadeInUp = () => {
            if (isMobile()) return; // Do not run on mobile

            const elements = document.querySelectorAll(
                'h1, h2, h3, h4, h5, h6, p, time, ul li, ol li'
            );
            const startValue = 'top 80%';
            const endValue = 'top 50%';

            elements.forEach((element) => {
                if (
                    element.classList.contains('no-animate') ||
                    element.classList.contains('animated-once') ||
                    excludedClasses.some((selector) => element.matches(selector))
                )
                    return;

                // If the element is already within the viewport start position on load,
                // set it immediately to its final state and mark it as animated-once to
                // prevent the entrance animation from running. This avoids double
                // animations for items visible on initial load.
                try {
                    const rect = element.getBoundingClientRect();
                    const viewportHeight =
                        window.innerHeight || document.documentElement.clientHeight;

                    // If any part of the element intersects the viewport on load, show it instantly.
                    // This is a simple intersection check: element's top is less than the viewport
                    // bottom and its bottom is greater than the viewport top.
                    if (rect.top < viewportHeight && rect.bottom > 0) {
                        gsap.set(element, { opacity: 1, y: 0 });
                        element.classList.add('animated-once');
                        return; // skip ScrollTrigger animation
                    }
                } catch {
                    // In unlikely cases (cross-origin SVGs etc.) fallback to normal animation
                    console.warn('scrollFadeInUp: viewport check failed');
                }

                gsap.fromTo(
                    element,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: element,
                            start: startValue,
                            end: endValue,
                            once: true,
                            onEnter: () => element.classList.add('animated-once'),
                        },
                    }
                );
            });
        };

        // Animate all media elements (images, SVGs, videos) as you scroll to them
        const scrollFadeIn = () => {
            if (isMobile()) return; // Do not run on mobile

            const mediaElements = document.querySelectorAll('img, svg, video, a');
            const startValue = 'top 80%';
            const endValue = 'top 50%';

            mediaElements.forEach((element) => {
                if (
                    element.classList.contains('no-animate') ||
                    element.classList.contains('animated-once') ||
                    excludedClasses.some((selector) => element.matches(selector))
                )
                    return;

                // If the media element intersects the viewport on load, show it immediately
                try {
                    const rect = element.getBoundingClientRect();
                    const viewportHeight =
                        window.innerHeight || document.documentElement.clientHeight;
                    if (rect.top < viewportHeight && rect.bottom > 0) {
                        gsap.set(element, { opacity: 1 });
                        element.classList.add('animated-once');
                        return;
                    }
                } catch {
                    console.warn('scrollFadeIn: viewport check failed');
                }

                gsap.fromTo(
                    element,
                    { opacity: 0 },
                    {
                        opacity: 1,
                        duration: 0.6,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: element,
                            start: startValue,
                            end: endValue,
                            once: true,
                            onEnter: () => element.classList.add('animated-once'),
                        },
                    }
                );
            });
        };

        // Initial setup
        scrollFadeInUp();
        scrollFadeIn();

        // Apply aria-labels to animated CTA links using text from nested spans
        const applyAriaLabelsToAnimatedLinks = () => {
            const ctaLinks = document.querySelectorAll('a .arrow-cta-regular');
            ctaLinks.forEach((span) => {
                const linkElement = span.closest('a');
                if (linkElement && span.textContent.trim()) {
                    // Get text and clean up any extra whitespace
                    const ariaText = span.textContent.trim().replace(/\s+/g, ' ');
                    linkElement.setAttribute('aria-label', ariaText);
                }
            });
        };

        function animateArrowCtaButtons() {
            if (isDesktop()) {
                document.querySelectorAll('.wp-block-button__link').forEach((btn) => {
                    // Prevent double initialization
                    if (btn._arrowCtaAnimated) return;
                    btn._arrowCtaAnimated = true;

                    const regular = btn.querySelector('.arrow-cta-regular');
                    const bold = btn.querySelector('.arrow-cta-bold');
                    if (!regular || !bold) return;

                    // If on .single-case-studies page, animate immediately
                    if (document.body.classList.contains('single-case_studies')) {
                        // Split both spans into characters
                        const splitRegular = new SplitText(regular, {
                            type: 'chars',
                            aria: 'none',
                        });
                        const splitBold = new SplitText(bold, { type: 'chars', aria: 'none' });

                        // Hide bold chars initially
                        gsap.set(splitBold.chars, { opacity: 0, y: 10 });

                        let toBoldTimeline, toRegularTimeline;

                        btn.addEventListener('mouseenter', () => {
                            if (toRegularTimeline) toRegularTimeline.kill();
                            toBoldTimeline = gsap.timeline();
                            splitRegular.chars.forEach((char, i) => {
                                toBoldTimeline.to(
                                    char,
                                    {
                                        opacity: 0,
                                        y: -10,
                                        duration: 0.25,
                                        delay: i * 0.04,
                                        ease: 'power2.in',
                                    },
                                    0
                                );
                                toBoldTimeline.to(
                                    splitBold.chars[i],
                                    {
                                        opacity: 1,
                                        y: 0,
                                        duration: 0.25,
                                        delay: i * 0.04 + 0.08,
                                        ease: 'power2.out',
                                    },
                                    0
                                );
                            });
                        });

                        btn.addEventListener('mouseleave', () => {
                            if (toBoldTimeline && toBoldTimeline.isActive()) {
                                toBoldTimeline.progress(1, false);
                            }
                            toRegularTimeline = gsap.timeline();
                            splitBold.chars.forEach((char, i) => {
                                toRegularTimeline.to(
                                    char,
                                    {
                                        opacity: 0,
                                        y: 10,
                                        duration: 0.2,
                                        delay: i * 0.04,
                                        ease: 'power2.in',
                                    },
                                    0
                                );
                                toRegularTimeline.to(
                                    splitRegular.chars[i],
                                    {
                                        opacity: 1,
                                        y: 0,
                                        duration: 0.2,
                                        delay: i * 0.04 + 0.08,
                                        ease: 'power2.out',
                                    },
                                    0
                                );
                            });
                        });
                        return; // Skip ScrollTrigger setup
                    }

                    // Use GSAP ScrollTrigger to initialize only when in view
                    ScrollTrigger.create({
                        trigger: btn,
                        start: 'top 95%',
                        once: true,
                        onEnter: () => {
                            // Split both spans into characters
                            const splitRegular = new SplitText(regular, {
                                type: 'chars',
                                aria: 'none',
                            });
                            const splitBold = new SplitText(bold, { type: 'chars', aria: 'none' });

                            // Hide bold chars initially
                            gsap.set(splitBold.chars, { opacity: 0, y: 10 });

                            let toBoldTimeline, toRegularTimeline;

                            btn.addEventListener('mouseenter', () => {
                                if (toRegularTimeline) toRegularTimeline.kill();
                                toBoldTimeline = gsap.timeline();
                                splitRegular.chars.forEach((char, i) => {
                                    toBoldTimeline.to(
                                        char,
                                        {
                                            opacity: 0,
                                            y: -10,
                                            duration: 0.25,
                                            delay: i * 0.04,
                                            ease: 'power2.in',
                                        },
                                        0
                                    );
                                    toBoldTimeline.to(
                                        splitBold.chars[i],
                                        {
                                            opacity: 1,
                                            y: 0,
                                            duration: 0.25,
                                            delay: i * 0.04 + 0.08,
                                            ease: 'power2.out',
                                        },
                                        0
                                    );
                                });
                            });

                            btn.addEventListener('mouseleave', () => {
                                if (toBoldTimeline && toBoldTimeline.isActive()) {
                                    toBoldTimeline.progress(1, false);
                                }
                                toRegularTimeline = gsap.timeline();
                                splitBold.chars.forEach((char, i) => {
                                    toRegularTimeline.to(
                                        char,
                                        {
                                            opacity: 0,
                                            y: 10,
                                            duration: 0.2,
                                            delay: i * 0.04,
                                            ease: 'power2.in',
                                        },
                                        0
                                    );
                                    toRegularTimeline.to(
                                        splitRegular.chars[i],
                                        {
                                            opacity: 1,
                                            y: 0,
                                            duration: 0.2,
                                            delay: i * 0.04 + 0.08,
                                            ease: 'power2.out',
                                        },
                                        0
                                    );
                                });
                            });
                        },
                    });
                });
            }
        }

        animateArrowCtaButtons();

        // Reset Arrow CTA button animations when archive grid filter is clicked
        function resetArrowCtaAnimations() {
            // Kill all existing timelines and reset SplitText
            document.querySelectorAll('.wp-block-button__link').forEach((btn) => {
                if (btn._arrowCtaAnimated) {
                    const regular = btn.querySelector('.arrow-cta-regular');
                    const bold = btn.querySelector('.arrow-cta-bold');

                    if (regular && bold) {
                        // Kill any existing timelines
                        gsap.killTweensOf([regular, bold]);

                        // Reset split text if it exists
                        if (regular._splitText) {
                            regular._splitText.revert();
                            delete regular._splitText;
                        }
                        if (bold._splitText) {
                            bold._splitText.revert();
                            delete bold._splitText;
                        }

                        // Reset inline styles
                        gsap.set([regular, bold], { clearProps: 'all' });

                        // Mark as not animated so it can be re-initialized
                        btn._arrowCtaAnimated = false;
                    }
                }
            });

            // Re-initialize the animations after a brief delay
            setTimeout(() => {
                animateArrowCtaButtons();
            }, 100);
        }

        // Listen for clicks on archive grid filter buttons
        document.addEventListener('click', (e) => {
            if (
                e.target.classList.contains('archive-grid-filter-button') ||
                e.target.closest('.archive-grid-filter-button')
            ) {
                // Reset Arrow CTA animations after 1 second
                setTimeout(resetArrowCtaAnimations, 1000);
            }
        });

        if (document.getElementById('work')) {
            const postGrid = new PostGrid('work');
            postGrid.init();
        }

        // Add a class to .menu-primary when the page starts scrolling
        const primaryMenu = document.querySelector('.menu-primary');
        if (primaryMenu) {
            window.addEventListener('scroll', () => {
                if (window.scrollY > 0) {
                    document.body.classList.add('menu-primary-scrolled');
                } else {
                    document.body.classList.remove('menu-primary-scrolled');
                }
            });
        }

        // Function to handle menu trigger and mobile menu link animation
        function setupMenuPrimaryTrigger() {
            const menuTrigger = document.querySelector('.menu-primary-trigger');
            if (!menuTrigger) return;

            // If there's an existing handler, remove it to prevent duplicates
            if (menuTrigger._menuHandler) {
                menuTrigger.removeEventListener('click', menuTrigger._menuHandler);
                delete menuTrigger._menuHandler;
            }

            const handler = (e) => {
                e.preventDefault();

                // Ensure the trigger is clickable (clear any pointer-events overrides)
                menuTrigger.style.pointerEvents = '';

                document.body.classList.toggle('menu-primary-open');
                menuTrigger.classList.toggle('menu-primary-triggered');

                // Mobile: close all open submenus
                if (window.matchMedia('(max-width: 1023px)').matches) {
                    if (!document.body.classList.contains('menu-primary-open')) {
                        document.querySelectorAll('.sub-menu.show').forEach((submenu) => {
                            submenu.classList.remove('show');
                        });
                        document.body.style.overflow = '';
                    }
                } else {
                    // Desktop click: ensure ripples are active
                    initMenuPrimaryListRipple();
                }
            };

            menuTrigger.addEventListener('click', handler);
            menuTrigger._menuHandler = handler;
        }

        // Initialize the menu trigger functionality
        setupMenuPrimaryTrigger();

        // Animate .menu-primary-trigger-line drawing in left to right with stagger
        function animateMenuPrimaryTriggerLines() {
            const lines = document.querySelectorAll('.menu-primary-trigger-line');
            if (lines.length) {
                gsap.to(lines, {
                    width: '100%',
                    duration: 0.2,
                    ease: 'power2.out',
                    stagger: 0.06,
                });
            }
        }

        animateMenuPrimaryTriggerLines();

        // Toggle a class on .hero-sticky when 25% of it is scrolled
        function handleHeroSticky() {
            const heroSticky = document.querySelector('.hero-cover');
            const sidebarSticky = document.querySelector('.sidebar-sticky');
            const caseStudyColumns = document.querySelector(
                '.case-study-columns, .campaign-columns'
            );
            const positionFixedElements = document.querySelector('.position-fixed-elements');

            function moveSidebarSticky() {
                if (window.matchMedia('(min-width: 1200px)').matches) {
                    if (
                        sidebarSticky &&
                        positionFixedElements &&
                        !positionFixedElements.contains(sidebarSticky)
                    ) {
                        positionFixedElements.appendChild(sidebarSticky);
                    }
                } else {
                    // On mobile, sidebarSticky should be the first div inside .case-study-columns
                    if (
                        sidebarSticky &&
                        caseStudyColumns &&
                        caseStudyColumns.firstElementChild !== sidebarSticky
                    ) {
                        caseStudyColumns.insertBefore(
                            sidebarSticky,
                            caseStudyColumns.firstElementChild
                        );
                    }
                }
            }

            moveSidebarSticky();

            window.addEventListener('resize', moveSidebarSticky);

            if (window.matchMedia('(min-width: 1200px)').matches) {
                if (heroSticky && sidebarSticky) {
                    const heroStickyHeight = heroSticky.offsetHeight;
                    const heroStickyTrigger = heroSticky.offsetTop + heroStickyHeight * 0.25;

                    // Create the black overlay once
                    function initializeBlackOverlay() {
                        if (!sidebarSticky.querySelector('.sidebar-sticky__black-overlay')) {
                            // Get the inner HTML (exclude the overlay itself and button if it exists)
                            const overlayContent = Array.from(sidebarSticky.children)
                                .filter(
                                    (child) =>
                                        !child.classList.contains(
                                            'sidebar-sticky__black-overlay'
                                        ) && !child.classList.contains('wp-block-buttons')
                                )
                                .map((el) => el.cloneNode(true));

                            const blackOverlay = document.createElement('div');
                            blackOverlay.className = 'sidebar-sticky__black-overlay';
                            blackOverlay.setAttribute('aria-hidden', 'true');
                            blackOverlay.inert = true;
                            overlayContent.forEach((el) =>
                                blackOverlay.appendChild(el.cloneNode(true))
                            );
                            blackOverlay.querySelectorAll('[id]').forEach((element) => {
                                element.removeAttribute('id');
                            });
                            blackOverlay.querySelectorAll('a, button, [tabindex]').forEach((element) => {
                                element.setAttribute('tabindex', '-1');
                            });
                            sidebarSticky.appendChild(blackOverlay);
                        }
                    }

                    // Initialize overlay on first check
                    initializeBlackOverlay();

                    // Use the content boundary, not hidden white UI injected by forms or reCAPTCHA.
                    function getWhiteBackgroundPosition() {
                        return caseStudyColumns
                            ? caseStudyColumns.getBoundingClientRect().top + window.scrollY
                            : window.innerHeight * 3;
                    }

                    const updateSidebar = () => {
                        if (window.scrollY >= heroStickyTrigger) {
                            sidebarSticky.classList.add('sidebar-sticky-scrolled');
                        } else {
                            sidebarSticky.classList.remove('sidebar-sticky-scrolled');
                        }

                        // Update the black overlay clip-path based on scroll position
                        const blackOverlay = sidebarSticky.querySelector(
                            '.sidebar-sticky__black-overlay'
                        );
                        if (blackOverlay) {
                            const whiteBackgroundPos = getWhiteBackgroundPosition();
                            const sidebarRect = sidebarSticky.getBoundingClientRect();
                            const sidebarViewportTop = sidebarRect.top;
                            const distanceToWhiteBackground =
                                whiteBackgroundPos - (window.scrollY + sidebarViewportTop);

                            // Calculate what percentage of the sidebar has entered the white background
                            let clipPercentage = 0;
                            if (distanceToWhiteBackground < sidebarRect.height) {
                                // We've started entering the white background
                                clipPercentage =
                                    ((sidebarRect.height - distanceToWhiteBackground) /
                                        sidebarRect.height) *
                                    100;
                                clipPercentage = Math.min(Math.max(clipPercentage, 0), 100);
                            }

                            // Use clip-path to reveal the black text from top as it enters white background
                            const clipValue = 100 - clipPercentage; // Invert so we clip from top
                            blackOverlay.style.clipPath = `inset(${clipValue}% 0 0 0)`;

                            // Keep the original text accessible while its decorative black copy is visible.
                            // But exclude the button/CTA since it has complex styling
                            const textElements = Array.from(sidebarSticky.children).filter(
                                (child) =>
                                    !child.classList.contains('sidebar-sticky__black-overlay') &&
                                    !child.classList.contains('wp-block-buttons')
                            );

                            if (clipPercentage >= 100) {
                                // Transparent text keeps links and headings available to assistive technology.
                                textElements.forEach((child) => {
                                    child.style.color = 'transparent';
                                });
                            } else {
                                // Not fully visible - show white text
                                textElements.forEach((child) => {
                                    child.style.color = '';
                                });
                            }

                            // Change button color based on scroll position
                            // Instantly black when entering white background, white otherwise
                            const buttonLink =
                                sidebarSticky.querySelector('.wp-block-button__link');
                            if (buttonLink) {
                                if (clipPercentage >= 10) {
                                    // Has entered white background area - use black
                                    buttonLink.style.color = 'rgb(0, 0, 0)';
                                    buttonLink.style.borderColor = 'rgb(0, 0, 0)';
                                } else {
                                    // Still in original area - use white (or let CSS handle it)
                                    buttonLink.style.color = '';
                                    buttonLink.style.borderColor = '';
                                }
                            }
                        }

                        if (caseStudyColumns) {
                            const caseStudyEnd =
                                caseStudyColumns.getBoundingClientRect().bottom - 60;
                            if (window.innerHeight >= caseStudyEnd) {
                                sidebarSticky.classList.add('sidebar-sticky-end');
                            } else {
                                sidebarSticky.classList.remove('sidebar-sticky-end');
                            }
                        }
                    };

                    window.addEventListener('scroll', updateSidebar, { passive: true });
                    updateSidebar();
                    return updateSidebar;
                }
            }
        }

        const updateHeroSticky = handleHeroSticky();

        if (isDesktop()) {
            // create the scrollSmoother before your scrollTriggers
            ScrollSmoother.create({
                smooth: 1, // how long (in seconds) it takes to "catch up" to the native scroll position
                effects: true, // looks for data-speed and data-lag attributes on elements
                onUpdate: updateHeroSticky,
            });
        }

        // Parallax effect for images in .image-staggered, we should make this more reusable once other components are done
        const parallaxEffect = () => {
            if (isDesktop()) {
                const staggeredImages = document.querySelectorAll('.image-staggered img');

                if (staggeredImages.length === 2) {
                    const [image1, image2] = staggeredImages;

                    gsap.fromTo(
                        image1,
                        { yPercent: 100 },
                        {
                            yPercent: 0,
                            ease: 'power1.out',
                            scrollTrigger: {
                                trigger: '.image-staggered',
                                start: 'top 150%',
                                end: 'bottom top',
                                scrub: true,
                            },
                        }
                    );

                    gsap.fromTo(
                        image2,
                        { yPercent: 50 },
                        {
                            yPercent: 0,
                            ease: 'power1.out',
                            scrollTrigger: {
                                trigger: '.image-staggered',
                                start: 'top 150%',
                                end: 'bottom top',
                                scrub: true,
                            },
                        }
                    );
                }
            }
        };

        // Animate SVGs in each li of .is-style-list-animated
        function animateLists() {
            const animatedLists = document.querySelectorAll('.is-style-list-animated li svg rect');
            animatedLists.forEach((svg) => {
                gsap.fromTo(
                    svg,
                    { drawSVG: '0%' },
                    {
                        drawSVG: '100%',
                        duration: 4,
                        ease: 'power1.inOut',
                        scrollTrigger: {
                            trigger: svg,
                            start: 'top 80%',
                            end: 'top 50%',
                            scrub: 2,
                        },
                    }
                );
            });
        }

        // Animate SVGs in .is-style-group-animated
        function animateGroups() {
            const groupAnimatedPaths = document.querySelectorAll(
                '.is-style-group-animated svg path'
            );
            groupAnimatedPaths.forEach((path) => {
                gsap.fromTo(
                    path,
                    { drawSVG: '0%' },
                    {
                        drawSVG: '100%',
                        duration: 4,
                        ease: 'power1.inOut',
                        scrollTrigger: {
                            trigger: path,
                            start: 'top 80%',
                            end: 'top 50%',
                            scrub: 2,
                        },
                    }
                );
            });
        }

        // Animate SVGs in .is-style-quote-animated-square
        function animateStat() {
            const quoteAnimatedSquares = document.querySelectorAll(
                '.is-style-quote-animated-square polygon'
            );
            quoteAnimatedSquares.forEach((path, i) => {
                gsap.fromTo(
                    path,
                    { drawSVG: '0%' },
                    {
                        drawSVG: '100%',
                        duration: 4,
                        ease: 'power2.inOut',
                        scrollTrigger: {
                            trigger: path,
                            start: `top ${90 - i * 40}%`, // Less aggressive stagger, starts later
                            end: 'top 60%',
                            scrub: 2, // Lower scrub for smoother feel
                        },
                    }
                );
            });
        }

        // Animate .text-image-split .wp-block-image from -180px to 0
        const animateTextImageSplitImage = () => {
            if (isMobile()) return;

            const textImageSplit = document.querySelector('.text-image-split');
            const images = document.querySelectorAll('.text-image-split .wp-block-image');
            const textColumn = document.querySelector(
                '.text-image-split .wp-block-column:first-child'
            );

            if (!textImageSplit) return;

            images.forEach((image) => {
                gsap.fromTo(
                    image,
                    { y: '25%' },
                    {
                        y: 0,
                        duration: 1,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: textImageSplit,
                            start: 'top top',
                            end: 'bottom 25%',
                            scrub: 1,
                        },
                    }
                );
            });

            // Animate first column text at different speed
            if (textColumn) {
                gsap.fromTo(
                    textColumn,
                    { y: '5%' },
                    {
                        y: '-15%',
                        duration: 2,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: textImageSplit,
                            start: 'top top',
                            end: 'bottom 25%',
                            scrub: 1.5,
                        },
                    }
                );
            }
        };

        // Initial setup
        parallaxEffect();
        animateLists();
        animateGroups();
        animateStat();
        animateTextImageSplitImage();

        function toggleHeroElementsAndVideo() {
            const desktopEl = document.querySelector('.hero-element-desktop');
            const mobileEl = document.querySelector('.hero-element-mobile');
            const pauseBtns = document.querySelectorAll('.hero-home .hero-element-pause');
            const heroFullClose = document.querySelector('.hero-full-close');
            const vimeoIframe = () =>
                document.querySelector('.hero-video-full iframe[src*="vimeo"]');
            const isDesktop = window.matchMedia('(min-width: 1023px)').matches;

            // Detach src to data-src for both videos (once)
            [desktopEl, mobileEl].forEach((el) => {
                const video = el && el.querySelector('video');
                if (video && !video.dataset.src) {
                    video.dataset.src = video.src;
                    video.removeAttribute('src');
                }
            });

            // Set src for the correct video
            function setSrc(el, shouldSet) {
                const video = el && el.querySelector('video');
                if (!video) return;
                if (shouldSet && !video.src && video.dataset.src) {
                    video.src = video.dataset.src;
                } else if (!shouldSet && video.src) {
                    video.removeAttribute('src');
                }
            }
            setSrc(desktopEl, isDesktop);
            setSrc(mobileEl, !isDesktop);

            // Toggle play/pause for all hero videos and update pause button state/text
            function toggleAllVideosAndButtons() {
                document.querySelectorAll('.hero-home .wp-block-video video').forEach((video) => {
                    video.paused ? video.play() : video.pause();
                });
                pauseBtns.forEach((btn) => {
                    btn.classList.toggle('hero-element-pause-paused');
                    btn.textContent =
                        btn.textContent.trim().toLowerCase() === 'pause' ? 'Play' : 'Pause';
                });
            }

            // Pause/play handler for pause buttons
            pauseBtns.forEach((pauseBtn) => {
                // The pause control is authored as a <p>, so it has no native role
                // and does not activate on Enter/Space. Expose it to assistive tech
                // as a real button and add keyboard activation to match native
                // <button> behavior. The visible label toggles Pause/Play, which
                // serves as the accessible name/state.
                pauseBtn.setAttribute('role', 'button');
                if (!pauseBtn.hasAttribute('tabindex')) {
                    pauseBtn.setAttribute('tabindex', '0');
                }

                pauseBtn.onclick = (e) => {
                    e.stopPropagation();
                    toggleAllVideosAndButtons();
                };

                pauseBtn.onkeydown = (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleAllVideosAndButtons();
                    }
                };
            });

            // Unified handler for toggling hero video full overlay and Vimeo play/pause
            function toggleHeroVideoFull(e) {
                if (isDesktop && e.target.closest('.hero-element-pause')) return;
                document.body.classList.toggle('hero-video-full-open');
                const iframe = vimeoIframe();
                if (iframe && iframe.contentWindow) {
                    const isOpen = document.body.classList.contains('hero-video-full-open');
                    iframe.contentWindow.postMessage(
                        JSON.stringify({ method: isOpen ? 'play' : 'pause' }),
                        '*'
                    );
                }
                toggleAllVideosAndButtons();
            }

            // Attach overlay toggle handlers
            const desktopTriggers = document.querySelectorAll(
                '.hero-home .hero-element .wp-block-video'
            );
            // CHANGED: Use the whole .hero-element-mobile as trigger instead of just .hero-element-trigger
            const mobileTriggers = document.querySelectorAll('.hero-home .hero-element-mobile');
            (isDesktop ? desktopTriggers : mobileTriggers).forEach((el) => {
                el.onclick = toggleHeroVideoFull;
            });

            // Close overlay handler
            if (heroFullClose) {
                heroFullClose.onclick = (e) => {
                    e.preventDefault();
                    document.body.classList.remove('hero-video-full-open');
                    const iframe = vimeoIframe();
                    if (iframe && iframe.contentWindow) {
                        iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
                    }
                    toggleAllVideosAndButtons();
                };
            }
        }

        // Initial setup
        toggleHeroElementsAndVideo();

        // Animate padding-left, padding-right, and margin-top of .hero-home .hero-element figure
        const animateHeroElementFigure = () => {
            if (isDesktop()) {
                const heroElementFigure = document.querySelector(
                    '.hero-home .hero-element-desktop'
                );
                if (!heroElementFigure) return; // Prevent error if not found

                const videoElement = heroElementFigure.querySelector('video');
                const pauseBtn = document.querySelector('.hero-element-pause');
                if (!videoElement) return; // Prevent error if no video

                gsap.to(heroElementFigure, {
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    marginTop: `-${document.querySelector('.main-headline').offsetHeight}px`,
                    ease: 'power1.out',
                    scrollTrigger: {
                        trigger: '.hero-home-wrapper',
                        start: 'top top',
                        end: 'bottom top',
                        scrub: true,
                        pin: true,
                        invalidateOnRefresh: true, // Recalculate pin spacer on refresh
                        onEnter: () => {
                            if (videoElement.paused) {
                                videoElement.play();
                                if (pauseBtn) {
                                    pauseBtn.classList.remove('hero-element-pause-paused');
                                    pauseBtn.textContent = 'Pause';
                                }
                            }
                        },
                        onLeaveBack: () => {
                            if (!videoElement.paused) {
                                videoElement.pause();
                                if (pauseBtn) {
                                    pauseBtn.classList.add('hero-element-pause-paused');
                                    pauseBtn.textContent = 'Play';
                                }
                            }
                        },
                    },
                });
            }

            if (isMobile()) {
                const heroElementFigure = document.querySelector('.hero-home .hero-element-mobile');
                if (!heroElementFigure) return; // Prevent error if not found

                const mediaElement = heroElementFigure.querySelector('video, img');
                const pauseBtn = document.querySelector('.hero-element-pause');
                if (!mediaElement) return; // Prevent error if no media

                if (mediaElement.tagName === 'VIDEO') {
                    if (pauseBtn) pauseBtn.classList.add('hero-element-pause-visible');
                }

                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: '.hero-home-wrapper',
                        start: 'top top',
                        end: 'bottom top',
                        scrub: 0.5, // Enable scrub for smoother animation
                        pin: false, // Never pin on mobile
                        onEnter: () => {
                            if (mediaElement.tagName === 'VIDEO' && mediaElement.paused) {
                                mediaElement.play();
                                if (pauseBtn) {
                                    pauseBtn.classList.remove('hero-element-pause-paused');
                                    pauseBtn.textContent = 'Pause';
                                }
                            }
                        },
                        onLeaveBack: () => {
                            if (mediaElement.tagName === 'VIDEO' && !mediaElement.paused) {
                                mediaElement.pause();
                                if (pauseBtn) {
                                    pauseBtn.classList.add('hero-element-pause-paused');
                                    pauseBtn.textContent = 'Play';
                                }
                            }
                        },
                    },
                });

                tl.to(
                    heroElementFigure,
                    {
                        paddingLeft: '0px',
                        paddingRight: '0px',
                        marginTop: '-40px',
                        duration: 1,
                        ease: 'power1.out',
                    },
                    0
                );

                const triggerEl = document.querySelector('.hero-element-trigger');
                if (triggerEl) {
                    tl.fromTo(
                        triggerEl,
                        { left: '70px' },
                        {
                            left: '30px',
                            duration: 1,
                            ease: 'power1.out',
                        },
                        0
                    );
                }
            }
        };

        // Initial setup
        animateHeroElementFigure();

        // Animate .slides-vertical slides stacking on top of each other
        function initSlidesVertical() {
            if (isMobile()) return;

            const slides = document.querySelectorAll('.slides-vertical .slide');
            if (slides.length === 0) return;

            slides.forEach((slide, index) => {
                const isLastSlide = index === slides.length - 1;

                ScrollTrigger.create({
                    trigger: slide,
                    start: 'top top',
                    end: isLastSlide ? 'bottom bottom' : 'bottom top',
                    pin: true,
                    pinSpacing: false,
                });
            });
        }

        initSlidesVertical();

        // Function to initialize home intro rotators
        function initHomeIntroRotators() {
            // Move all paragraphs in .home-intro-rotators to the end of .home-intro-rotator
            const homeIntroRotators = document.querySelector('.home-intro-rotators');
            const homeIntroRotator = document.querySelector('.home-intro-rotator');

            if (homeIntroRotators && homeIntroRotator) {
                homeIntroRotator.appendChild(homeIntroRotators);
            }

            // Wrap inner content of .home-intro-rotators with .home-intro-rotators-inner
            document.querySelectorAll('.home-intro-rotators').forEach((rotatorsEl) => {
                if (!rotatorsEl.querySelector('.home-intro-rotators-inner')) {
                    const wrapper = document.createElement('div');
                    wrapper.className = 'home-intro-rotators-inner';
                    while (rotatorsEl.firstChild) {
                        wrapper.appendChild(rotatorsEl.firstChild);
                    }
                    rotatorsEl.appendChild(wrapper);
                }
            });

            const rotators = document.querySelectorAll('.home-intro-rotators');

            rotators.forEach((rotator) => {
                // Use the new inner wrapper for selecting paragraphs
                const items = rotator.querySelectorAll('.home-intro-rotators-inner p');
                let currentIndex = 0;
                let intervalId = null;
                let hasStarted = false;

                // Function to show the next item
                const showNextItem = () => {
                    if (currentIndex >= items.length - 1) {
                        // Last item is now visible, stop rotating
                        clearInterval(intervalId);
                        return;
                    }
                    const currentItem = items[currentIndex];
                    currentIndex = currentIndex + 1;
                    const nextItem = items[currentIndex];

                    currentItem.classList.remove('active');
                    currentItem.classList.add('exiting');
                    nextItem.classList.add('active');

                    setTimeout(() => {
                        currentItem.classList.remove('exiting');
                    }, 500); // Match the CSS transition duration
                };

                // Initialize the first item as active
                if (items.length > 0) {
                    items[currentIndex].classList.add('active');

                    // Only start rotating when scrolled into view
                    ScrollTrigger.create({
                        trigger: rotator,
                        start: 'top 90%',
                        once: true,
                        onEnter: () => {
                            if (!hasStarted) {
                                hasStarted = true;
                                intervalId = setInterval(showNextItem, 2000); // Change every 2 seconds
                            }
                        },
                    });
                }
            });
        }

        initHomeIntroRotators();

        // Enhanced Skip Link functionality for accessibility
        const skipLink = document.querySelector('.skip-link');
        const menuPrimary = document.querySelector('.menu-primary');

        if (skipLink && menuPrimary) {
            // Ensure skip link is always first in tab order
            skipLink.setAttribute('tabindex', '1');

            skipLink.addEventListener('click', (e) => {
                e.preventDefault();

                // Get the height of the menu
                const menuHeight = menuPrimary.offsetHeight;

                // Find the main content area - try multiple selectors
                let target = document.querySelector('h1');

                if (target) {
                    // Make target focusable if it isn't already
                    if (!target.hasAttribute('tabindex')) {
                        target.setAttribute('tabindex', '-1');
                    }

                    // Add aria-label for screen readers if it's not a heading
                    if (
                        !target.matches('h1, h2, h3, h4, h5, h6') &&
                        !target.hasAttribute('aria-label')
                    ) {
                        target.setAttribute('aria-label', 'Main content area');
                    }

                    // Use GSAP's scrollTo plugin to scroll to the target position with an offset
                    gsap.to(window, {
                        scrollTo: {
                            y: target,
                            offsetY: menuHeight, // Offset by the menu height
                        },
                        duration: 0.5,
                        ease: 'power2.out',
                        onComplete: () => {
                            // Focus the target element after scrolling is complete
                            target.focus();

                            // Announce to screen readers
                            const announcement = document.createElement('div');
                            announcement.setAttribute('aria-live', 'polite');
                            announcement.setAttribute('aria-atomic', 'true');
                            announcement.className = 'visually-hidden';
                            announcement.textContent = 'Skipped to main content';
                            document.body.appendChild(announcement);

                            // Remove announcement after it's been read
                            setTimeout(() => {
                                document.body.removeChild(announcement);
                            }, 1000);
                        },
                    });
                }
            });

            // Handle focus visibility for skip link
            skipLink.addEventListener('focus', () => {
                skipLink.style.position = 'absolute';
                skipLink.style.top = '100px';
            });

            skipLink.addEventListener('blur', () => {
                skipLink.style.position = 'absolute';
                skipLink.style.top = '-100px';
            });
        }

        // When you click .features-group, trigger the link inside each .wp-block-button__link
        const featuresGroups = document.querySelectorAll('.features-group');
        featuresGroups.forEach((group) => {
            group.addEventListener('click', (e) => {
                // Prevent default if the click is on a link already
                if (e.target.closest('.wp-block-button__link')) return;
                const links = group.querySelectorAll('.wp-block-button__link');
                links.forEach((link) => {
                    // Simulate a click on each link
                    link.click();
                });
            });
        });

        // Menu ripple effect for .menu-primary-list a
        // We split initialization and teardown so the ripple handlers can be removed
        // when the mobile menu is open or when on mobile view, and re-initialized
        // when switching back to desktop.
        function initMenuPrimaryListRipple() {
            if (!isDesktop()) return;
            const links = document.querySelectorAll('.menu-primary-list > li > a');
            links.forEach((link) => {
                // If a SplitText instance exists from the mobile menu animation,
                // revert it so DOM/text returns to normal before initializing ripples.
                if (link._splitText) {
                    try {
                        link._splitText.revert();
                    } catch {
                        // ignore revert errors
                    }
                    delete link._splitText;
                }

                if (link._rippleAnimated) return; // already initialized

                if (!link.querySelector('.menu-link-text')) {
                    link.innerHTML = `<span class="menu-link-text">${link.textContent.trim()}</span>`;
                }

                // Create overlay element
                const overlay = document.createElement('span');
                overlay.className = 'menu-ripple-overlay';
                link.prepend(overlay);

                let rippleTween, colorTween;

                // Store handlers so they can be removed later
                const onMouseEnter = (e) => {
                    const rect = link.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const y = e.clientY - rect.top;
                    overlay.style.left = `${x - overlay.offsetWidth / 2}px`;
                    overlay.style.top = `${y - overlay.offsetHeight / 2}px`;

                    if (rippleTween) rippleTween.kill();
                    gsap.set(overlay, { scale: 0, opacity: 1 });
                    rippleTween = gsap.to(overlay, {
                        scale: 2.2,
                        opacity: 1,
                        duration: 2,
                        ease: 'power2.out',
                    });

                    if (colorTween) colorTween.kill();
                    colorTween = gsap.to(link.querySelector('.menu-link-text'), {
                        color: '#000',
                        duration: 2,
                        ease: 'power2.out',
                    });
                };

                const onMouseLeave = () => {
                    if (rippleTween) rippleTween.kill();
                    rippleTween = gsap.to(overlay, {
                        scale: 0,
                        opacity: 0,
                        duration: 0.5,
                        ease: 'power2.in',
                    });

                    const parent = link.parentElement;
                    const targetColor =
                        parent && parent.classList.contains('active') ? '#000' : '#fff';
                    if (colorTween) colorTween.kill();
                    colorTween = gsap.to(link.querySelector('.menu-link-text'), {
                        color: targetColor,
                        duration: 0.5,
                        ease: 'power2.in',
                        onComplete: () => {
                            link.querySelector('.menu-link-text').style.color = '';
                        },
                    });
                };

                link.addEventListener('mouseenter', onMouseEnter);
                link.addEventListener('mouseleave', onMouseLeave);

                // Mark initialized and store handlers/overlay for teardown
                link._rippleAnimated = true;
                link._rippleData = { overlay, onMouseEnter, onMouseLeave };
            });
        }

        function destroyMenuPrimaryListRipple() {
            const links = document.querySelectorAll('.menu-primary-list a');
            links.forEach((link) => {
                if (!link._rippleAnimated) return;
                const data = link._rippleData || {};
                if (data.onMouseEnter) link.removeEventListener('mouseenter', data.onMouseEnter);
                if (data.onMouseLeave) link.removeEventListener('mouseleave', data.onMouseLeave);
                if (data.overlay && data.overlay.parentElement === link) data.overlay.remove();
                // Clear inline styles
                const text = link.querySelector('.menu-link-text');
                if (text) text.removeAttribute('style');
                // delete markers
                delete link._rippleAnimated;
                delete link._rippleData;
            });
        }

        // Initialize ripples if appropriate on load
        initMenuPrimaryListRipple();

        // Initialize aria-expanded on anchors that have a following sub-menu
        function initMenuSubMenuAriaExpanded() {
            const menuLinks = document.querySelectorAll('.menu-primary-list a');
            menuLinks.forEach((link) => {
                const nextSibling = link.nextElementSibling;
                if (nextSibling && nextSibling.classList.contains('sub-menu')) {
                    link.setAttribute('aria-haspopup', 'menu');
                    link.setAttribute('aria-expanded', 'false');
                }
            });
        }

        // Monitor sub-menu "show" class and update parent anchor aria-expanded
        function monitorSubMenuShowClass() {
            const subMenus = document.querySelectorAll('.menu-primary-list .sub-menu');
            subMenus.forEach((subMenu) => {
                const parentLink = subMenu.previousElementSibling;
                if (!parentLink || parentLink.tagName !== 'A') return;

                // Use MutationObserver to efficiently watch for class changes
                const observer = new MutationObserver(() => {
                    const hasShowClass = subMenu.classList.contains('show');
                    parentLink.setAttribute('aria-expanded', hasShowClass ? 'true' : 'false');
                });

                // Start observing class changes
                observer.observe(subMenu, { attributes: true, attributeFilter: ['class'] });

                // Set initial state
                const hasShowClass = subMenu.classList.contains('show');
                parentLink.setAttribute('aria-expanded', hasShowClass ? 'true' : 'false');
            });
        }

        // Initialize and monitor sub-menu aria-expanded states
        initMenuSubMenuAriaExpanded();
        monitorSubMenuShowClass();

        // Ripple effect for .js-filters li (only if .js-filters exists)
        function animateJsFiltersRipple() {
            if (isDesktop()) {
                const jsFilters = document.querySelector('.js-filters');
                if (!jsFilters) return;

                jsFilters.querySelectorAll('li').forEach((li) => {
                    if (li._rippleAnimated) return;
                    li._rippleAnimated = true;

                    if (!li.querySelector('.menu-link-text')) {
                        li.innerHTML = `<span class="menu-link-text">${li.textContent.trim()}</span>`;
                    }

                    const overlay = document.createElement('span');
                    overlay.className = 'menu-ripple-overlay';
                    li.prepend(overlay);

                    let rippleTween, colorTween;

                    li.addEventListener('mouseenter', (e) => {
                        // Set all other menu-link-text to white immediately, except .cs-filter
                        jsFilters.querySelectorAll('li').forEach((otherLi) => {
                            if (otherLi !== li) {
                                const otherText = otherLi.querySelector('.menu-link-text');
                                if (otherText && !otherLi.classList.contains('cs-filter')) {
                                    gsap.killTweensOf(otherText);
                                    otherText.style.color = '#fff';
                                }
                            }
                        });

                        const rect = li.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        const y = e.clientY - rect.top;

                        overlay.style.left = `${x - overlay.offsetWidth / 2}px`;
                        overlay.style.top = `${y - overlay.offsetHeight / 2}px`;

                        if (rippleTween) rippleTween.kill();
                        gsap.set(overlay, { scale: 0, opacity: 1 });
                        rippleTween = gsap.to(overlay, {
                            scale: 2.2,
                            opacity: 1,
                            duration: 2,
                            ease: 'power2.out',
                        });

                        if (colorTween) colorTween.kill();
                        colorTween = gsap.to(li.querySelector('.menu-link-text'), {
                            color: '#000',
                            duration: 0.2,
                            ease: 'power2.out',
                        });
                    });

                    li.addEventListener('mouseleave', () => {
                        if (rippleTween) rippleTween.kill();
                        rippleTween = gsap.to(overlay, {
                            scale: 0,
                            opacity: 0,
                            duration: 0.5,
                            ease: 'power2.in',
                        });

                        if (colorTween) colorTween.kill();

                        // On leave, set color instantly to correct state
                        const text = li.querySelector('.menu-link-text');
                        if (li.classList.contains('cs-filter')) {
                            text.style.color = '#000';
                        } else {
                            text.style.color = '#fff';
                        }
                    });

                    // Listen for class changes to clear inline styles when cs-filter is removed
                    const observer = new MutationObserver(() => {
                        const text = li.querySelector('.menu-link-text');
                        if (text) text.removeAttribute('style');
                        overlay.removeAttribute('style');
                    });
                    observer.observe(li, { attributes: true });
                });

                // When a filter is clicked, set all to white except the new .cs-filter (which stays black)
                jsFilters.addEventListener('click', (e) => {
                    setTimeout(() => {
                        jsFilters.querySelectorAll('li').forEach((li) => {
                            const text = li.querySelector('.menu-link-text');
                            gsap.killTweensOf(text);
                            if (li.classList.contains('cs-filter')) {
                                text.style.color = '#000';
                            } else {
                                text.style.color = '#fff';
                            }
                        });
                    }, 0);
                });
            }
        }

        animateJsFiltersRipple();

        // Duplicate for .work-single-categories li
        function animateWorkSingleCategoriesRipple() {
            if (isDesktop()) {
                document.querySelectorAll('.work-single-categories li').forEach((li) => {
                    // Prevent double initialization
                    if (li._rippleAnimated) return;
                    li._rippleAnimated = true;

                    // Use GSAP ScrollTrigger to initialize only when in view
                    ScrollTrigger.create({
                        trigger: li,
                        start: 'top 90%',
                        once: true,
                        onEnter: () => {
                            // Wrap text if not already wrapped
                            if (!li.querySelector('.menu-link-text')) {
                                li.innerHTML = `<span class="menu-link-text">${li.textContent.trim()}</span>`;
                            }
                            const overlay = document.createElement('span');
                            overlay.className = 'menu-ripple-overlay';
                            li.prepend(overlay);

                            let rippleTween, colorTween;

                            li.addEventListener('mouseenter', (e) => {
                                const rect = li.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const y = e.clientY - rect.top;

                                overlay.style.left = `${x - overlay.offsetWidth / 2}px`;
                                overlay.style.top = `${y - overlay.offsetHeight / 2}px`;

                                if (rippleTween) rippleTween.kill();
                                gsap.set(overlay, { scale: 0, opacity: 1 });
                                rippleTween = gsap.to(overlay, {
                                    scale: 2.2,
                                    opacity: 1,
                                    duration: 2,
                                    ease: 'power2.out',
                                });

                                if (colorTween) colorTween.kill();
                                colorTween = gsap.to(li.querySelector('.menu-link-text'), {
                                    color: '#000',
                                    duration: 2,
                                    ease: 'power2.out',
                                });
                            });

                            li.addEventListener('mouseleave', () => {
                                if (rippleTween) rippleTween.kill();
                                rippleTween = gsap.to(overlay, {
                                    scale: 0,
                                    opacity: 0,
                                    duration: 0.5,
                                    ease: 'power2.in',
                                });

                                if (colorTween && colorTween.isActive()) {
                                    colorTween.progress(1, false);
                                }
                                // .work-single-categories li never has cs-filter, so always revert to #000
                                colorTween = gsap.to(li.querySelector('.menu-link-text'), {
                                    color: '#000',
                                    duration: 0.5,
                                    ease: 'power2.in',
                                });
                            });

                            // Listen for class changes to clear inline styles if needed
                            const observer = new MutationObserver(() => {
                                const text = li.querySelector('.menu-link-text');
                                if (text) text.removeAttribute('style');
                                overlay.removeAttribute('style');
                            });
                            observer.observe(li, { attributes: true });
                        },
                    });
                });
            }
        }

        animateWorkSingleCategoriesRipple();

        // Duplicate for .query-loop .wp-block-categories-list li
        function animateQueryLoopCategoriesListRipple() {
            if (isDesktop()) {
                document
                    .querySelectorAll('.query-loop .wp-block-categories-list li')
                    .forEach((li) => {
                        // Prevent double initialization
                        if (li._rippleAnimated) return;
                        li._rippleAnimated = true;

                        // Use GSAP ScrollTrigger to initialize only when in view
                        ScrollTrigger.create({
                            trigger: li,
                            start: 'top 90%',
                            once: true,
                            onEnter: () => {
                                // Wrap text if not already wrapped
                                if (!li.querySelector('.menu-link-text')) {
                                    li.innerHTML = `<span class="menu-link-text">${li.textContent.trim()}</span>`;
                                }
                                const overlay = document.createElement('span');
                                overlay.className = 'menu-ripple-overlay';
                                li.prepend(overlay);

                                let rippleTween, colorTween;

                                li.addEventListener('mouseenter', (e) => {
                                    const rect = li.getBoundingClientRect();
                                    const x = e.clientX - rect.left;
                                    const y = e.clientY - rect.top;

                                    overlay.style.left = `${x - overlay.offsetWidth / 2}px`;
                                    overlay.style.top = `${y - overlay.offsetHeight / 2}px`;

                                    if (rippleTween) rippleTween.kill();
                                    gsap.set(overlay, { scale: 0, opacity: 1 });
                                    rippleTween = gsap.to(overlay, {
                                        scale: 2.2,
                                        opacity: 1,
                                        duration: 2,
                                        ease: 'power2.out',
                                    });

                                    if (colorTween) colorTween.kill();
                                    colorTween = gsap.to(li.querySelector('.menu-link-text'), {
                                        color: '#000',
                                        duration: 2,
                                        ease: 'power2.out',
                                    });
                                });

                                li.addEventListener('mouseleave', () => {
                                    if (rippleTween) rippleTween.kill();
                                    rippleTween = gsap.to(overlay, {
                                        scale: 0,
                                        opacity: 0,
                                        duration: 0.5,
                                        ease: 'power2.in',
                                    });

                                    if (colorTween && colorTween.isActive()) {
                                        colorTween.progress(1, false);
                                    }
                                    // Always revert to #000
                                    colorTween = gsap.to(li.querySelector('.menu-link-text'), {
                                        color: '#000',
                                        duration: 0.5,
                                        ease: 'power2.in',
                                    });
                                });

                                // Listen for class changes to clear inline styles if needed
                                const observer = new MutationObserver(() => {
                                    const text = li.querySelector('.menu-link-text');
                                    if (text) text.removeAttribute('style');
                                    overlay.removeAttribute('style');
                                });
                                observer.observe(li, { attributes: true });
                            },
                        });
                    });
            }
        }

        animateQueryLoopCategoriesListRipple();

        // Animate .query-loop .wp-block-post columns
        const animateQueryLoopColumns = () => {
            if (isDesktop()) {
                const columns = document.querySelectorAll(
                    '.query-loop .wp-block-post-template .wp-block-post'
                );
                if (columns.length > 0) {
                    gsap.fromTo(
                        columns,
                        { marginTop: 120 },
                        {
                            marginTop: 0,
                            duration: 0.8,
                            ease: 'power2.out',
                            stagger: 0.2,
                            scrollTrigger: {
                                trigger: columns[0].closest('.query-loop'),
                                start: 'top 85%',
                                once: true,
                                scrub: true,
                            },
                        }
                    );
                }
            }
        };

        animateQueryLoopColumns();

        // Animate .work-single elements
        const animateWorkSingles = () => {
            const excludedIndexes = new Set([0, 5]);
            const getWorkSingleImage = (single) => single.querySelector('.work-single-header img');

            const clearAnimatedTarget = (target) => {
                const tween = target._workSingleTween;

                if (tween) {
                    if (tween.scrollTrigger) {
                        tween.scrollTrigger.kill();
                    }
                    tween.kill();
                    delete target._workSingleTween;
                } else {
                    gsap.killTweensOf(target);
                }

                target.style.transform = '';
                target.style.removeProperty('will-change');
            };

            const clearWorkSingleAnimation = (single) => {
                clearAnimatedTarget(single);
                clearAnimatedTarget(getWorkSingleImage(single));
            };

            const createWorkSingleTween = (target, trigger, startY, scrub) => {
                return gsap.fromTo(
                    target,
                    { y: startY },
                    {
                        y: 0,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger,
                            start: 'top 85%',
                            once: true,
                            scrub,
                        },
                    }
                );
            };

            const allSingles = Array.from(document.querySelectorAll('.work-single'));
            allSingles.forEach(clearWorkSingleAnimation);

            // If .work-singles-filtered exists, clear styles and skip animation
            if (!isDesktop() || document.querySelector('.work-singles-filtered')) {
                return;
            }

            // Exclude nth-child 1 and 6
            const singles = allSingles.filter((_, index) => !excludedIndexes.has(index));
            if (!singles.length) return;

            singles.forEach((single, index) => {
                // Alternate direction: even index goes down, odd index goes up, all end at 0
                const singleStartY = index % 2 === 0 ? 60 : -60;
                const scrubSpeed = 1 + index * 0.2;

                single._workSingleTween = createWorkSingleTween(
                    single,
                    single,
                    singleStartY,
                    scrubSpeed
                );

                // Image always moves from -60 to 0, regardless of index
                const image = getWorkSingleImage(single);
                image._workSingleTween = createWorkSingleTween(
                    image,
                    single,
                    -60,
                    scrubSpeed * 1.2
                );
            });
        };

        animateWorkSingles();

        const queryLoopEl = document.querySelector('.query-loop');
        if (queryLoopEl) {
            // For each post item in the query loop
            queryLoopEl.querySelectorAll('.wp-block-post').forEach((postEl) => {
                const linkEl = postEl.querySelector('.query-loop-link');
                const contentEls = postEl.querySelectorAll(
                    '.query-loop-content .wp-block-button__link'
                );
                if (linkEl && contentEls.length) {
                    const href = linkEl.getAttribute('href');
                    if (href) {
                        contentEls.forEach((contentEl) => contentEl.setAttribute('href', href));
                    }
                    linkEl.remove();
                }
            });
        }

        const heroVideoFull = document.querySelector('.hero-video-full');
        const positionFixedElements = document.querySelector('.position-fixed-elements');
        if (heroVideoFull && positionFixedElements) {
            positionFixedElements.appendChild(heroVideoFull);
        }

        const socialLinks = document.querySelectorAll('.wp-block-social-link-anchor');
        if (socialLinks) {
            socialLinks.forEach((link) => {
                const label = link.querySelector('.wp-block-social-link-label');
                link.setAttribute('title', `Link to Pace ${label.textContent} profile`);
            });
        }

        // Observe for dynamically added elements only within smooth-content
        const postGridElement = document.getElementById('smooth-content');
        if (postGridElement) {
            const observer = new MutationObserver((mutationsList) => {
                let shouldAnimate = false;
                for (const mutation of mutationsList) {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        shouldAnimate = true;
                        break;
                    }
                }
                if (shouldAnimate) {
                    animateArrowCtaButtons();
                    animateWorkSingleCategoriesRipple();
                    animateWorkSingles();
                    applyAriaLabelsToAnimatedLinks();
                }
            });

            observer.observe(postGridElement, {
                childList: true,
                subtree: true,
            });
        }

        // Combine all resize events into one throttled handler
        let globalResizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(globalResizeTimeout);
            globalResizeTimeout = setTimeout(() => {
                // Kill all existing ScrollTriggers
                ScrollTrigger.getAll().forEach((trigger) => trigger.kill());

                if (isMobile()) {
                    // Remove inline styles and delete canvas elements for .heavy-chevron-cta .wp-block-button__link
                    const buttons = document.querySelectorAll(
                        '.heavy-chevron-cta .wp-block-button__link'
                    );
                    buttons.forEach((button) => {
                        // Remove inline styles
                        button.removeAttribute('style');

                        // Delete canvas elements
                        const canvas = button.querySelector('canvas');
                        if (canvas) {
                            canvas.remove();
                        }
                    });

                    // Clear styles applied by scrollFadeInUp and scrollFadeIn
                    const elements = document.querySelectorAll(
                        'h1, h2, h3, h4, h5, h6, p, ul li, ol li, img, svg, video, a'
                    );
                    elements.forEach((element) => {
                        gsap.killTweensOf(element);
                        element.style.opacity = '';
                        element.style.transform = '';
                    });

                    // On mobile, destroy desktop ripples to avoid hover handlers on touch devices
                    destroyMenuPrimaryListRipple();
                } else {
                    // Re-run animation setup for desktop
                    scrollFadeInUp();
                    scrollFadeIn();
                    // On desktop, initialize menu ripples
                    initMenuPrimaryListRipple();
                }

                handleHeroSticky();

                // Re-run other animations
                animateArrowCtaButtons();
                animateWorkSingleCategoriesRipple();
                animateWorkSingles();
                parallaxEffect();
                animateHeroElementFigure();
                animateLists();
                animateGroups();
                animateStat();
                animateTextImageSplitImage();
                setupMenuPrimaryTrigger();
                initSlidesVertical();

                // Re-initialize carousels and update pagination on resize
                initTimelineCarousel();
                splideCarouselSelectors.forEach((selector) => {
                    initSplideCarousel(selector);
                });

                // Refresh ScrollTrigger positions
                ScrollTrigger.refresh();
            }, 150); // Throttle delay in ms
        });

        // Add tabindex to videos
        const videos = document.querySelectorAll('video');
        videos.forEach((video) => {
            video.setAttribute('tabindex', '0');
        });

        const heroContainer = document.querySelector('.hero-element-desktop');

        if (heroContainer) {
            const video = heroContainer.querySelector('video');
            const videoTitle = heroContainer.getAttribute('video-title');

            if (video) {
                if (videoTitle) {
                    video.setAttribute('aria-label', videoTitle);
                }
            }
        }

        // Add legend tags to HubSpot form fieldsets for accessibility
        const addLegendsToHubSpotFieldsets = () => {
            document.querySelectorAll('.hbspt-form fieldset').forEach((fieldset) => {
                if (fieldset.querySelector('legend')) return;

                const label = fieldset.querySelector(
                    'label:not(.hs-error-msg):not(.hs-form-booleancheckbox-display)'
                );
                const input = fieldset.querySelector('input, textarea, select');

                let legendText =
                    label?.textContent?.trim() ||
                    input?.placeholder?.trim() ||
                    (fieldset.classList.contains('form-columns-2')
                        ? 'Contact Information'
                        : fieldset.querySelector('input[type="checkbox"]')
                          ? 'Agreement'
                          : fieldset.querySelector('textarea')
                            ? 'Message'
                            : 'Form Section');

                if (legendText) {
                    const legend = document.createElement('legend');
                    legend.textContent = legendText;
                    legend.className = 'visually-hidden';
                    fieldset.insertBefore(legend, fieldset.firstChild);
                }
            });
        };

        addLegendsToHubSpotFieldsets();
        setTimeout(addLegendsToHubSpotFieldsets, 1000);

        new MutationObserver(() => {
            setTimeout(addLegendsToHubSpotFieldsets, 100);
        }).observe(document.body, { childList: true, subtree: true });

        // Adding tabindex to all elements on the home page
        if (document.body.classList.contains('home')) {
            const tabElements = document.querySelectorAll(
                'a, input, button, p, h1, h2, h3, li, img, video'
            );
            const skipLink = document.querySelector('.skip-link');

            for (const el of tabElements) {
                // Skip the skip-link element - it should remain tabindex="1"
                if (el === skipLink) {
                    continue;
                }

                // Skip elements hidden from assistive tech (e.g. an eyebrow folded
                // into an <h1>'s accessible name). Making an aria-hidden element
                // keyboard-focusable is an anti-pattern: it would be a tab stop that
                // screen readers cannot announce.
                if (el.closest('[aria-hidden="true"]')) {
                    continue;
                }

                if (!el.hasAttribute('tabindex')) {
                    el.setAttribute('tabindex', '0');
                }
            }
        }

        // setting tabindex order on Case Study page, reroute tabindex after 'view page' link
        if (document.body.classList.contains('single-case-studies')) {
            // Exclude skip-link from the initial selection to avoid any conflicts
            const allElements = document.querySelectorAll('a:not(.skip-link), input, button');
            const heroLink = document.querySelector(
                '.sidebar-sticky .wp-block-buttons .wp-block-button .wp-block-button__link'
            );
            const splitIndex = 6;
            let tabIndexCounter = 3;

            allElements.forEach((el, i) => {
                if (i < splitIndex) {
                    el.setAttribute('tabindex', '2'); // Start at 2 since skip-link is 1
                } else {
                    el.setAttribute('tabindex', tabIndexCounter.toString());
                    tabIndexCounter++;
                }
            });

            if (heroLink) {
                heroLink.setAttribute('aria-label', 'View the site (opens in a new tab)');
            }
        }

        // Accessibility: ensure the Contact Us select has an aria-label
        const contactUsContainer = document.querySelector('.contact-us-main-content');
        if (contactUsContainer) {
            const applySelectAria = () => {
                const form = contactUsContainer.querySelector('form');
                const select = form
                    ? form.querySelector('select')
                    : contactUsContainer.querySelector('select');

                if (select) {
                    if (!select.hasAttribute('aria-label')) {
                        select.setAttribute('aria-label', 'How can we help you?');
                    }
                    return true;
                }
                return false;
            };

            if (!applySelectAria()) {
                const observer = new MutationObserver(() => {
                    if (applySelectAria()) {
                        observer.disconnect();
                    }
                });
                observer.observe(contactUsContainer, { childList: true, subtree: true });
            }
        }

        // Make main h1 heading keyboard focusable on About Us and similar pages
        const makeMainHeadingFocusable = () => {
            const mainHeading = document.querySelector('h1.wp-block-heading');
            if (mainHeading && !mainHeading.hasAttribute('tabindex')) {
                mainHeading.setAttribute('tabindex', '0');
            }
        };

        // Make all section headings focusable if their section lacks interactive elements
        const makeSectionHeadingsFocusable = () => {
            // Find all major section containers and headings
            document.querySelectorAll('section, [class*="wp-block-group"]').forEach((section) => {
                // Skip if section has interactive elements (links, buttons, inputs, selects)
                const hasInteractive = section.querySelector(
                    'a, button, input, textarea, select, [role="button"]'
                );

                if (!hasInteractive) {
                    // Find the first heading in this section
                    const heading = section.querySelector('h1, h2, h3, h4, h5, h6');
                    if (heading && !heading.hasAttribute('tabindex')) {
                        heading.setAttribute('tabindex', '0');
                    }
                }
            });
        };

        // Remove tabindex from <a> links with the "animated-once" class
        const removeTabindexFromAnimatedLinks = () => {
            const animatedLinks = document.querySelectorAll('a.animated-once');
            animatedLinks.forEach((link) => {
                if (link.hasAttribute('tabindex')) {
                    link.removeAttribute('tabindex');
                }
            });
        };

        // Hide decorative SVG elements from screen readers
        const hideArrowCtaSvgs = () => {
            const arrowCtaSvgs = document.querySelectorAll('.arrow-cta-svg');
            arrowCtaSvgs.forEach((element) => {
                element.setAttribute('aria-hidden', 'true');
            });
        };

        // Initial removal and accessibility improvements
        makeMainHeadingFocusable();
        makeSectionHeadingsFocusable();
        hideArrowCtaSvgs();
        removeTabindexFromAnimatedLinks();
        applyAriaLabelsToAnimatedLinks();

        //Video embed functionality, to replace cover image with video embed container on button click
        document.querySelectorAll('.post-body-video').forEach((videoEmbed) => {
            const videoPlayButton = videoEmbed.querySelector('.post-body-video-preview');
            const iframe = videoEmbed.querySelector('iframe');
            const html5Video = videoEmbed.querySelector('video');

            // Accessibility: hide video/iframe until played
            [iframe, html5Video].forEach((el) => {
                if (el) {
                    el.setAttribute('aria-hidden', 'true');
                    el.setAttribute('tabindex', '-1');
                }
            });

            if (videoPlayButton) {
                videoPlayButton.setAttribute('tabindex', '0');
                videoPlayButton.setAttribute('role', 'button');
                videoPlayButton.setAttribute('aria-label', 'Play video');

                // Keyboard activation
                videoPlayButton.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        videoPlayButton.click();
                    }
                });

                videoPlayButton.addEventListener('click', (e) => {
                    e.preventDefault();

                    // Pause all other videos/iframes except the current one
                    document.querySelectorAll('.post-body-video').forEach((otherEmbed) => {
                        if (otherEmbed !== videoEmbed) {
                            const otherIframe = otherEmbed.querySelector('iframe');
                            const otherHtml5Video = otherEmbed.querySelector('video');

                            // Pause HTML5 video
                            if (otherHtml5Video && !otherHtml5Video.paused) {
                                otherHtml5Video.pause();
                            }
                            // Stop iframe video (reloads the src to stop playback)
                            if (otherIframe) {
                                // Remove autoplay=1 from the src if present
                                otherIframe.src = otherIframe.src
                                    .replace(/([?&])autoplay=1(&)?/, '$1')
                                    .replace(/[?&]$/, '');
                            }

                            // Remove playing class
                            otherEmbed.classList.remove('post-body-video-playing');

                            // Hide video/iframe for accessibility
                            [otherIframe, otherHtml5Video].forEach((el) => {
                                if (el) {
                                    el.setAttribute('aria-hidden', 'true');
                                    el.setAttribute('tabindex', '-1');
                                }
                            });
                            const otherVideoContainer = otherEmbed.querySelector('.is-type-video');
                            if (otherVideoContainer)
                                otherVideoContainer.setAttribute('tabindex', '-1');
                            // Show play button if hidden
                            const otherPlayBtn = otherEmbed.querySelector('.wp-block-button__link');
                            if (otherPlayBtn) otherPlayBtn.style.display = '';
                        }
                    });

                    videoEmbed.classList.add('post-body-video-playing');

                    // Show video/iframe for accessibility
                    [iframe, html5Video].forEach((el) => {
                        if (el) {
                            el.setAttribute('aria-hidden', 'false');
                            el.setAttribute('tabindex', '0');
                        }
                    });

                    const videoContainer = videoEmbed.querySelector('.is-type-video');
                    if (videoContainer) videoContainer.setAttribute('tabindex', '0');

                    setTimeout(() => {
                        if (iframe) iframe.focus();
                        if (html5Video) html5Video.focus();
                        const playBtn = videoEmbed.querySelector('.wp-block-button__link');
                        if (playBtn) playBtn.style.display = 'none';
                    }, 200);

                    // Autoplay logic
                    if (iframe) {
                        let src = iframe.src;
                        if (!src.includes('autoplay=1')) {
                            src += (src.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1';
                            iframe.src = src;
                        }
                    } else if (html5Video) {
                        html5Video.autoplay = true;
                        html5Video.muted = false;
                        html5Video.play();
                    }
                });
            }
        });
        // Submenu hover and click functionality
        function initDropdownMenuHover() {
            // Use both breakpoint and pointer detection for better reliability at zoom levels
            const isDesktopSize = window.matchMedia('(min-width: 1024px)').matches;
            const hasHoverCapability = window.matchMedia(
                '(hover: hover) and (pointer: fine)'
            ).matches;

            if (!isDesktopSize || !hasHoverCapability) return;

            document
                .querySelectorAll('.menu-primary-list > li.menu-item-has-children')
                .forEach((menuItem) => {
                    const submenu = menuItem.querySelector('.sub-menu');
                    const parentLink = menuItem.querySelector('a');
                    if (!submenu || !parentLink) return;

                    // Store bound functions for later removal
                    if (!menuItem._hoverHandlers) {
                        let hideTimeout = null;

                        menuItem._hoverHandlers = {
                            enter: () => {
                                if (hideTimeout) clearTimeout(hideTimeout);
                                submenu.classList.add('show');
                            },
                            leave: () => {
                                hideTimeout = setTimeout(() => {
                                    submenu.classList.remove('show');
                                    hideTimeout = null;
                                }, 150);
                            },
                        };

                        menuItem.addEventListener('mouseenter', menuItem._hoverHandlers.enter);
                        menuItem.addEventListener('mouseleave', menuItem._hoverHandlers.leave);

                        // Only prevent clicking the parent link if href is "#"
                        const href = parentLink.getAttribute('href');
                        if (href && href.includes('#')) {
                            parentLink._desktopClickHandler = (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            };
                            parentLink.addEventListener('click', parentLink._desktopClickHandler);
                        }
                    }
                });
        }

        function destroyDropdownMenuHover() {
            document
                .querySelectorAll('.menu-primary-list > li.menu-item-has-children')
                .forEach((menuItem) => {
                    if (menuItem._hoverHandlers) {
                        menuItem.removeEventListener('mouseenter', menuItem._hoverHandlers.enter);
                        menuItem.removeEventListener('mouseleave', menuItem._hoverHandlers.leave);
                        delete menuItem._hoverHandlers;
                    }
                });
        }

        // Inject toggle icons into menu items with children (desktop and mobile)
        function injectMenuToggleIcons() {
            document
                .querySelectorAll('.menu-primary-list > li.menu-item-has-children > a')
                .forEach((parentLink) => {
                    if (!parentLink.querySelector('.menu-toggle-icon')) {
                        const toggleIcon = document.createElement('div');
                        toggleIcon.className = 'menu-toggle-icon';
                        toggleIcon.innerHTML = '<span></span><span></span>';
                        parentLink.appendChild(toggleIcon);
                    }
                });
        }

        injectMenuToggleIcons();

        // Mobile click-only submenu functionality
        function initDropdownMenuClick() {
            const isDesktopSize = window.matchMedia('(min-width: 1024px)').matches;
            const hasHoverCapability = window.matchMedia(
                '(hover: hover) and (pointer: fine)'
            ).matches;

            if (isDesktopSize && hasHoverCapability) return;

            document
                .querySelectorAll('.menu-primary-list > li.menu-item-has-children')
                .forEach((menuItem) => {
                    const submenu = menuItem.querySelector('.sub-menu');
                    const parentLink = menuItem.querySelector('a');
                    if (!submenu || !parentLink) return;

                    // Store bound function for later removal
                    if (!menuItem._clickHandler) {
                        // On mobile: toggle submenu when clicking the toggle icon
                        // If href is "#", toggle submenu on any part of the link
                        // Otherwise, let the link navigate when clicked (unless icon clicked)
                        menuItem._clickHandler = (e) => {
                            const isHashLink = parentLink.getAttribute('href') === '#';

                            // If click is on the toggle icon, always toggle submenu
                            if (e.target.closest('.menu-toggle-icon')) {
                                e.preventDefault();
                                e.stopPropagation();
                                const isNowOpen = submenu.classList.toggle('show');
                                document.body.style.overflow = isNowOpen ? 'hidden' : '';
                                return;
                            }

                            // If it's a hash link, toggle submenu when clicking the parent link
                            if (isHashLink && e.target.closest('a') === parentLink) {
                                e.preventDefault();
                                e.stopPropagation();
                                const isNowOpen = submenu.classList.toggle('show');
                                document.body.style.overflow = isNowOpen ? 'hidden' : '';
                                return;
                            }

                            // Otherwise, let the link navigate (don't prevent default)
                        };

                        menuItem.addEventListener('click', menuItem._clickHandler);

                        // Close submenu when any submenu link is clicked
                        const submenuLinks = submenu.querySelectorAll('a');
                        submenuLinks.forEach((link) => {
                            if (!link._closeSubmenuHandler) {
                                link._closeSubmenuHandler = () => {
                                    submenu.classList.remove('show');
                                    document.body.style.overflow = '';
                                };
                                link.addEventListener('click', link._closeSubmenuHandler);
                            }
                        });
                    }
                });
        }

        function destroyDropdownMenuClick() {
            document
                .querySelectorAll('.menu-primary-list > li.menu-item-has-children')
                .forEach((menuItem) => {
                    if (menuItem._clickHandler) {
                        menuItem.removeEventListener('click', menuItem._clickHandler);
                        delete menuItem._clickHandler;
                    }

                    // Clean up submenu link handlers
                    const submenu = menuItem.querySelector('.sub-menu');
                    if (submenu) {
                        submenu.querySelectorAll('a').forEach((link) => {
                            if (link._closeSubmenuHandler) {
                                link.removeEventListener('click', link._closeSubmenuHandler);
                                delete link._closeSubmenuHandler;
                            }
                        });
                    }
                });
        }

        initDropdownMenuHover();
        initDropdownMenuClick();

        let dropdownResizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(dropdownResizeTimeout);
            dropdownResizeTimeout = setTimeout(() => {
                // Destroy all existing handlers
                destroyDropdownMenuHover();
                destroyDropdownMenuClick();

                // Clear body overflow and close any open submenus
                document.body.style.overflow = '';
                document.querySelectorAll('.sub-menu.show').forEach((submenu) => {
                    submenu.classList.remove('show');
                });

                // Reinject icons to ensure they're always present
                injectMenuToggleIcons();

                // Re-initialize ARIA attributes for menu items with submenus
                initMenuSubMenuAriaExpanded();
                monitorSubMenuShowClass();

                // Reinitialize with new breakpoint checks
                initDropdownMenuHover();
                initDropdownMenuClick();
            }, 250);
        });

        // Final enforcement: Ensure skip-link is ALWAYS the first tab stop
        // This runs last to override any other tabindex assignments
        const finalSkipLink = document.querySelector('.skip-link');
        if (finalSkipLink) {
            finalSkipLink.setAttribute('tabindex', '1');

            // Also ensure it's properly positioned in the DOM (it should be first in the navigation)
            const menuPrimaryInner = document.querySelector('.menu-primary-inner');
            if (menuPrimaryInner && finalSkipLink.parentElement === menuPrimaryInner) {
                // If skip-link is not the first child, move it to be first
                if (menuPrimaryInner.firstElementChild !== finalSkipLink) {
                    menuPrimaryInner.insertBefore(
                        finalSkipLink,
                        menuPrimaryInner.firstElementChild
                    );
                }
            }

            // Ensure skip-link can receive focus
            finalSkipLink.style.position = 'absolute';
            finalSkipLink.style.left = '20px';
            finalSkipLink.style.zIndex = '1000';
        }

        function updateMasonryColumnsCount() {
            const masonryColumns = document.querySelector('.masonry-columns');
            if (masonryColumns) {
                const cards = masonryColumns.querySelectorAll('.masonry-card');
                masonryColumns.classList.forEach((cls) => {
                    if (cls.startsWith('masonry-columns-count-')) {
                        masonryColumns.classList.remove(cls);
                    }
                });
                masonryColumns.classList.add(`masonry-columns-count-${cards.length}`);
            }
        }

        // Initial count
        updateMasonryColumnsCount();

        // Recount when cards are changed dynamically
        const masonryColumns = document.querySelector('.masonry-columns');
        if (masonryColumns) {
            const observer = new MutationObserver(() => {
                updateMasonryColumnsCount();
            });
            observer.observe(masonryColumns, { childList: true, subtree: true });
        }
    } // Close initializeAnimations function
});

// Logo Showcase - Duplicate logos for seamless infinite animation
document.addEventListener('DOMContentLoaded', function () {
    const logoRows = document.querySelectorAll('.logo-showcase .logo-row');

    logoRows.forEach((row) => {
        const figures = Array.from(row.querySelectorAll('figure'));

        figures.forEach((figure) => {
            const clone = figure.cloneNode(true);
            row.appendChild(clone);
        });
    });
});

// Initialize timeline carousel with decade-based pagination
function initTimelineCarousel() {
    const timelineCarousels = document.querySelectorAll('.carousel-timeline .splide');

    timelineCarousels.forEach((splideElement) => {
        if (splideElement.splide) return; // Already initialized

        // Remove any existing pagination from a previous initialization
        const existingPagination = splideElement.querySelector('.carousel-timeline-pagination');
        if (existingPagination) {
            existingPagination.remove();
        }

        const splide = new Splide(splideElement, {
            type: 'slide',
            arrows: true,
            autoplay: false,
            pagination: false,
            autoWidth: false,
            perPage: 1,
            keyboard: false, // Disable Splide's keyboard navigation to avoid conflicts with button clicks
        });

        const slides = splideElement.querySelectorAll('.splide__slide');
        const labels = [];

        // Initialize slides: only make meaningful content elements (headings and paragraphs) within active slide tabbable
        slides.forEach((slide, idx) => {
            const contentElements = slide.querySelectorAll('h1, h2, h3, h4, h5, h6, p');

            if (idx === 0) {
                // Active slide: content is tabbable
                contentElements.forEach((el) => {
                    el.setAttribute('tabindex', '0');
                });
            } else {
                // Inactive slides: content is out of tab order
                contentElements.forEach((el) => {
                    el.setAttribute('tabindex', '-1');
                });
            }
        });

        // Get labels from each slide's data-timeline-labels attribute
        slides.forEach((slide) => {
            const label = slide.getAttribute('data-timeline-labels');
            labels.push(label || `Slide ${labels.length + 1}`);
        });

        // Replace arrow SVGs with text labels
        const prevArrow = splideElement.querySelector('.splide__arrow--prev');
        const nextArrow = splideElement.querySelector('.splide__arrow--next');

        if (prevArrow) {
            prevArrow.innerHTML = 'Prev';
            prevArrow.classList.add('timeline-arrow');
        }
        if (nextArrow) {
            nextArrow.innerHTML = 'Next';
            nextArrow.classList.add('timeline-arrow');
        }

        // Create pagination container
        const paginationContainer = document.createElement('div');
        paginationContainer.className = 'carousel-timeline-pagination';

        // Create pagination dots with labels from slides
        labels.forEach((label, index) => {
            const dot = document.createElement('button');
            dot.className = 'timeline-dot';
            dot.setAttribute('aria-label', `Go to ${label}`);
            dot.textContent = label;
            if (index === 0) dot.classList.add('is-active');

            const goToSlide = () => {
                splide.go(index);
            };

            dot.addEventListener('click', goToSlide);

            paginationContainer.appendChild(dot);
        });

        // Insert pagination at the beginning of splide element (first in DOM for tab order)
        splideElement.insertBefore(paginationContainer, splideElement.firstChild);

        // Update active dot on slide change AND manage tab order for content elements
        splide.on('move', () => {
            // Update active dot styling
            paginationContainer.querySelectorAll('.timeline-dot').forEach((dot, idx) => {
                dot.classList.toggle('is-active', idx === splide.index);
            });

            // Update tab order: only meaningful content elements (headings and paragraphs) get tabindex 0
            slides.forEach((slide, idx) => {
                const contentElements = slide.querySelectorAll('h1, h2, h3, h4, h5, h6, p');

                if (idx === splide.index) {
                    // Active slide content: in tab order
                    contentElements.forEach((el) => {
                        el.setAttribute('tabindex', '0');
                    });
                } else {
                    // Inactive slide content: out of tab order
                    contentElements.forEach((el) => {
                        el.setAttribute('tabindex', '-1');
                    });
                }
            });
        });

        splide.mount();

        // Move arrows to the end of the splide element so they're last in tab order
        // This matches their visual position at the bottom of the carousel
        const arrowsWrapper = splideElement.querySelector('.splide__arrows');
        if (arrowsWrapper) {
            splideElement.appendChild(arrowsWrapper);
        }

        // Mark element as initialized to prevent duplicate instances
        splideElement.splide = splide;
    });
}

// Generic Splide Carousel initialization with custom pagination
function initSplideCarousel(selector, options = {}) {
    // Support both single selector and multiple elements
    const elements = document.querySelectorAll(selector);
    if (elements.length === 0) return;

    elements.forEach((element) => {
        // Handle both cases: selector is the splide element itself, or splide is a child
        const splideElement = element.classList.contains('splide')
            ? element
            : element.querySelector('.splide');

        if (!splideElement || splideElement.splide) return; // Skip if already initialized

        // Default options
        const defaultOptions = {
            perPage: 4,
            perMove: 4,
            gap: '30px',
            pagination: false,
            breakpoints: {
                1024: { perPage: 2, perMove: 2 },
                600: { perPage: 1, perMove: 1 },
            },
        };

        const splideOptions = { ...defaultOptions, ...options };
        const splide = new Splide(splideElement, splideOptions);

        const totalSlides = splideElement.querySelectorAll('.splide__slide').length;
        const desktopMediaQuery = window.matchMedia('(min-width: 993px)');
        let arrowWrapper = splideElement.querySelector('.splide__arrows');
        let customPaginationContainer = null;

        function toggleSplideControlsVisibility() {
            if (!arrowWrapper || !splideElement.contains(arrowWrapper)) {
                arrowWrapper = splideElement.querySelector('.splide__arrows');
            }
            const hideControls = desktopMediaQuery.matches && totalSlides <= 4;
            if (arrowWrapper) {
                if (hideControls) {
                    arrowWrapper.style.setProperty('display', 'none');
                } else {
                    arrowWrapper.style.removeProperty('display');
                }
            }
            if (customPaginationContainer) {
                if (hideControls) {
                    customPaginationContainer.style.setProperty('display', 'none');
                } else {
                    customPaginationContainer.style.removeProperty('display');
                }
            }
        }

        splide.mount();

        function updateCustomPagination() {
            const track = splideElement.querySelector('.splide__track');
            if (!track || !track.parentNode) return;

            let info = track.parentNode.querySelector('.splide__custom-pagination');
            if (!info) {
                info = document.createElement('div');
                info.className = 'splide__custom-pagination';
                const nextArrow = track.parentNode.querySelector('.splide__arrow--next');
                if (nextArrow) {
                    nextArrow.parentNode.insertBefore(info, nextArrow);
                } else {
                    track.parentNode.appendChild(info);
                }
            }
            customPaginationContainer = info;

            // Get current active perMove and perPage from Splide (respects responsive breakpoints)
            const currentPerMove = Math.max(
                1,
                splide.options.perMove || splideOptions.perMove || 1
            );
            const currentPerPage = splide.options.perPage || splideOptions.perPage || 4;

            // Calculate total positions based on perMove
            // Formula: (totalSlides - perPage) / perMove + 1
            const totalPositions = Math.ceil((totalSlides - currentPerPage) / currentPerMove) + 1;

            // Current position (1-indexed)
            const currentPosition = Math.floor(splide.index / currentPerMove) + 1;

            info.innerHTML = `<span class="splide__custom-current">${currentPosition}</span> of <span class="splide__custom-total">${totalPositions}</span>`;
            toggleSplideControlsVisibility();
        }

        splide.on('mounted', updateCustomPagination);
        splide.on('move', updateCustomPagination);
        splide.on('updated', updateCustomPagination);
        splide.on('refresh', updateCustomPagination);

        // Initial pagination display
        updateCustomPagination();

        // Move arrows to the end of the splide element so they're last in tab order
        // This matches their visual position at the bottom of the carousel
        const arrowsWrapper = splideElement.querySelector('.splide__arrows');
        if (arrowsWrapper) {
            splideElement.appendChild(arrowsWrapper);
        }

        return splide;
    });
}

// MOBILE VIEW - Drop Down Menu for Filters
if (document.body.classList.contains('page-template-page-work')) {
    let selectionInitialized = false;
    let filtersSelect, filterSelected, filterDropDown, hiddenInput, filterDropDownListItems;
    let currentFocus = -1;

    function optionClickHandler(e) {
        e.stopPropagation();
        selectOption(this);
    }

    function initializeFiltersSelect() {
        filtersSelect = document.querySelector('.filters-selection');
        if (!filtersSelect) return;

        filterSelected = filtersSelect.querySelector('.filters-selection-item');
        filterDropDown = filtersSelect.querySelector('.filters-selection-options');
        hiddenInput = filtersSelect.querySelector('input[type="hidden"]');
        filterDropDownListItems = filterDropDown.querySelectorAll('li');
        currentFocus = -1;

        addAttributesToOptions();
        addADAAttributes();

        filterSelected.addEventListener('click', togglefilterDropDown);

        filterDropDownListItems.forEach((item) => {
            item.addEventListener('click', optionClickHandler);
        });

        filtersSelect.addEventListener('keydown', handleKeydown);
        document.addEventListener('click', handleOutsideClick);

        selectionInitialized = true;
    }

    function selectOption(item) {
        filterSelected.textContent = item.textContent;
        filterSelected.setAttribute(
            'aria-label',
            `Selected filter: ${item.textContent}. Press Enter or Space to open dropdown.`
        );

        if (hiddenInput) {
            hiddenInput.value = item.dataset.value;
        }
        filterDropDown.style.display = 'none';
        filtersSelect.setAttribute('aria-expanded', 'false');

        // Reset focus to the main button
        filterSelected.focus();

        const jsFilters = document.querySelector('.js-filters');
        if (jsFilters) {
            const value = item.dataset.value;
            let li = jsFilters.querySelector(`li[data-service="${value}"]`);
            if (!li) {
                li = Array.from(jsFilters.querySelectorAll('li')).find(
                    (el) => el.textContent.trim() === item.textContent.trim()
                );
            }
            if (li) li.click();
        }
    }

    function destroyfiltersSelect() {
        if (!selectionInitialized || !filtersSelect) return;

        filterSelected.removeEventListener('click', togglefilterDropDown);
        filterDropDownListItems.forEach((item) => {
            item.removeEventListener('click', optionClickHandler);
        });

        filtersSelect.removeEventListener('keydown', handleKeydown);
        document.removeEventListener('click', handleOutsideClick);

        filterDropDown.style.display = 'none';
        filtersSelect.setAttribute('aria-expanded', 'false');

        selectionInitialized = false;
    }

    function togglefilterDropDown() {
        const isOpen = filterDropDown.style.display === 'block';
        filterDropDown.style.display = isOpen ? 'none' : 'block';
        filtersSelect.setAttribute('aria-expanded', !isOpen);

        if (!isOpen) {
            // When opening, reset focus to first item
            currentFocus = 0;
            setFocus();
        } else {
            // When closing, return focus to the button
            filterSelected.focus();
            currentFocus = -1;
        }
    }

    function handleKeydown(e) {
        const isOpen = filterDropDown.style.display === 'block';

        // Handle keyboard navigation when dropdown is closed
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                togglefilterDropDown();
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                togglefilterDropDown();
            }
            return;
        }

        // Handle keyboard navigation when dropdown is open
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                currentFocus = (currentFocus + 1) % filterDropDownListItems.length;
                setFocus();
                break;
            case 'ArrowUp':
                e.preventDefault();
                currentFocus =
                    (currentFocus - 1 + filterDropDownListItems.length) %
                    filterDropDownListItems.length;
                setFocus();
                break;
            case 'Home':
                e.preventDefault();
                currentFocus = 0;
                setFocus();
                break;
            case 'End':
                e.preventDefault();
                currentFocus = filterDropDownListItems.length - 1;
                setFocus();
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (currentFocus > -1) {
                    selectOption(filterDropDownListItems[currentFocus]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                filterDropDown.style.display = 'none';
                filtersSelect.setAttribute('aria-expanded', 'false');
                filterSelected.focus();
                currentFocus = -1;
                break;
            case 'Tab':
                // Allow tab to close dropdown and move to next element
                filterDropDown.style.display = 'none';
                filtersSelect.setAttribute('aria-expanded', 'false');
                currentFocus = -1;
                break;
        }
    }

    function setFocus() {
        filterDropDownListItems.forEach((item, index) => {
            item.classList.remove('focused');
            item.setAttribute('aria-selected', 'false');
            if (index === currentFocus) {
                item.classList.add('focused');
                item.setAttribute('aria-selected', 'true');
                // Scroll item into view if needed
                item.scrollIntoView({ block: 'nearest' });
            }
        });
    }

    function handleOutsideClick(e) {
        if (!filtersSelect.contains(e.target)) {
            filterDropDown.style.display = 'none';
            filtersSelect.setAttribute('aria-expanded', 'false');
            currentFocus = -1;
        }
    }

    function addAttributesToOptions() {
        if (!filterDropDown) return;

        filterDropDownListItems.forEach((item, index) => {
            if (!item.hasAttribute('data-value')) {
                item.setAttribute('data-value', index + 1);
            }
        });
    }

    function addADAAttributes() {
        if (!filtersSelect || !filterSelected || !filterDropDown) return;

        // Add ARIA attributes to the main dropdown
        filtersSelect.setAttribute('role', 'combobox');
        filtersSelect.setAttribute('aria-haspopup', 'listbox');
        filtersSelect.setAttribute('aria-expanded', 'false');

        // Make the selection button focusable and add appropriate attributes
        filterSelected.setAttribute('tabindex', '0');
        filterSelected.setAttribute('role', 'button');
        filterSelected.setAttribute(
            'aria-label',
            `Selected filter: ${filterSelected.textContent}. Press Enter or Space to open dropdown.`
        );

        // Add ARIA attributes to the dropdown
        filterDropDown.setAttribute('role', 'listbox');
        filterDropDown.setAttribute('aria-label', 'Filter options');

        // Add ARIA attributes to each option
        filterDropDownListItems.forEach((item, index) => {
            item.setAttribute('role', 'option');
            item.setAttribute('tabindex', '-1');
            item.setAttribute('aria-selected', 'false');
            item.setAttribute('aria-label', `Filter by ${item.textContent}`);
            item.setAttribute('id', `filter-option-${index}`);
        });
    }

    function checkWindowSize() {
        if (window.innerWidth < 1200) {
            if (!selectionInitialized) {
                initializeFiltersSelect();
            }
        } else {
            destroyfiltersSelect();
        }
    }

    checkWindowSize();

    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(checkWindowSize, 150);
    });
}

// Splide carousel selectors to initialize
const splideCarouselSelectors = [
    '.archive-carousel-splide',
    '.carousel-awards .wp-block-pacecore-blocks-carousel',
];

// Initialize all Splide carousels
document.addEventListener('DOMContentLoaded', function () {
    // Initialize timeline carousel first (has special pagination)
    initTimelineCarousel();

    // Initialize standard carousels
    splideCarouselSelectors.forEach((selector) => {
        initSplideCarousel(selector);
    });
});

// Accessibility: an eyebrow/kicker (styled like a heading but authored as a <p>)
// is a label for the <h1> beneath it, not a separate section. Rather than expose
// it as its own heading, we fold it into the <h1>'s accessible name and hide the
// standalone eyebrow from assistive tech. Result: a single, clean <h1> that reads
// "<eyebrow>. <title>", with no duplicate announcement and correct hierarchy.
//
// Scope: only when the eyebrow introduces an <h1>. The eyebrow and <h1> are not
// always direct siblings (some templates nest the <h1> in a wrapper), so we match
// the first heading that follows the eyebrow in document order. Eyebrows that
// contain interactive content (e.g. a breadcrumb link) are left untouched, since
// hiding them would remove a real link from the accessibility tree.
document.addEventListener('DOMContentLoaded', function () {
    const eyebrowSelectors = ['.eyebrow', '.work-header-eyebrow', '.archive-header-eyebrow'];
    const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
    const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim();

    document.querySelectorAll(eyebrowSelectors.join(', ')).forEach((eyebrow) => {
        // Skip eyebrows that wrap focusable content; hiding them would drop a real
        // interactive element from the accessibility tree.
        if (eyebrow.querySelector('a, button, input, select, textarea')) {
            return;
        }

        // headings is in document order, so the first match is the nearest heading
        // that follows the eyebrow.
        const nextHeading = headings.find(
            (heading) =>
                eyebrow.compareDocumentPosition(heading) & Node.DOCUMENT_POSITION_FOLLOWING
        );

        if (
            !nextHeading ||
            nextHeading.tagName !== 'H1' ||
            nextHeading.hasAttribute('aria-label')
        ) {
            return;
        }

        const eyebrowText = normalize(eyebrow.textContent);
        const headingText = normalize(nextHeading.textContent);
        if (!eyebrowText || !headingText) {
            return;
        }

        nextHeading.setAttribute('aria-label', `${eyebrowText}. ${headingText}`);
        eyebrow.setAttribute('aria-hidden', 'true');
    });
});

// Accessibility: the desktop Work filters are authored as <li> elements with
// click handlers only, so keyboard users cannot focus or activate them. Expose
// each filter as a toggle button in the tab order, activate it on Enter/Space
// (delegating to the existing click handler), and keep aria-pressed in sync with
// the active-filter class so screen readers announce the current selection. The
// list is hidden on mobile (a separate combobox handles that breakpoint), where
// tabindex on hidden elements is inert, so this is safe to apply everywhere.
document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.js-filters').forEach((list) => {
        const items = list.querySelectorAll('li[data-service]');
        if (!items.length) {
            return;
        }

        list.setAttribute('role', 'group');
        if (!list.hasAttribute('aria-label')) {
            list.setAttribute('aria-label', 'Filter work by category');
        }

        const syncPressedState = () => {
            items.forEach((item) => {
                item.setAttribute(
                    'aria-pressed',
                    item.classList.contains('cs-filter') ? 'true' : 'false'
                );
            });
        };

        items.forEach((item) => {
            item.setAttribute('role', 'button');
            item.setAttribute('tabindex', '0');

            item.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    // Delegate to the existing click handler that performs the filter.
                    item.click();
                }
            });
        });

        syncPressedState();

        // The active filter (cs-filter) is toggled by the click handlers; mirror
        // that onto aria-pressed whenever an item's class list changes.
        const observer = new MutationObserver(syncPressedState);
        items.forEach((item) =>
            observer.observe(item, { attributes: true, attributeFilter: ['class'] })
        );
    });
});

